import React from 'react'
import { FormattedMessage } from 'react-intl'
import { COMPLIANCE_TYPES } from '@augmentt-dev/ui-components/src/reports/SecurityPosture/constants'
import { TASK_ACTIONS, TASK_TYPES } from '../hub-task-management/hub.constants'
import {
  APPLICATION_REGISTRATION_POLICY_WARNING_MESSAGE_ID,
  getDisableSmtpAuthCheckModalAlertContent,
  getDisableSmtpAuthMissingExchangeOnlineWarningMessageId,
  getDisableSmtpAuthStatusOptions,
  getApplicationRegistrationStatusOptions,
  getSecurityGroupCreationStatusOptions,
  getTenantCreationStatusOptions,
  getCalendarSharingCheckModalAlertContent,
  getCalendarSharingMissingExchangeOnlineWarningMessageId,
  getCalendarSharingStatusOptions,
  isMissingExchangeOnlineDisableSmtpAuthState,
  isMissingExchangeOnlineCalendarSharingState,
} from './utils'

export const PORTALS = {
  ENTRA: 'Entra',
  M365_ADMIN_CENTER: 'M365 Admin Center',
  DEFENDER: 'Defender',
  PURVIEW: 'Purview',
  EXCHANGE: 'Exchange',
  SHAREPOINT: 'SharePoint',
  TEAMS: 'Teams',
  INTUNE: 'Intune',
  DNS_EXTERNAL: 'DNS (external)',
  OTHER: 'Other',
}

const DISABLE_SMTP_AUTH_WARNING_MESSAGE_ID = 'SECURE.DISABLE_SMTP_AUTH_WARNING'
const DISABLE_SMTP_AUTH_MISSING_EXCHANGE_ADMIN_WARNING_MESSAGE_ID =
  'SECURE.DISABLE_SMTP_AUTH.MISSING_EXCHANGE_ADMIN_WARNING'
const CUSTOMER_LOCKBOX_MISSING_EXCHANGE_WRITE_PERMISSION_MESSAGE_ID =
  'SECURE.CUSTOMER_LOCKBOX.MISSING_EXCHANGE_WRITE_PERMISSION'
const SECURITY_DEFAULTS_CA_NOTICE_MESSAGE_ID = 'SECURE.SECURITY_DEFAULTS_ENABLED.CA_NOTICE'
const SECURITY_DEFAULTS_NO_CA_MFA_NOTICE_MESSAGE_ID = 'SECURE.SECURITY_DEFAULTS_DISABLED.NO_CA_MFA_NOTICE'

const isMissingDisableSmtpAuthExchangeAdminRole = (securityCheck) =>
  securityCheck?.configurationIssues?.missingGDAPInIntegration?.includes('Exchange Administrator')
const hasCustomerLockboxConfigurationIssues = (securityCheck) =>
  securityCheck?.configurationIssues?.missingScopesInIntegration?.length > 0 ||
  securityCheck?.configurationIssues?.missingGDAPInIntegration?.length > 0
const GUEST_USER_INVITATIONS = Object.freeze({
  // labels must match the status strings returned by the guestUserInvitationsRestricted Maester transform
  EVERYONE: {
    label: 'Allow everyone in the organization, including guest users, to invite external users.',
    value: 'everyone',
  },
  ADMINS_GUEST_INVITERS_AND_ALL_MEMBERS: {
    label: 'Allow the above admin roles and all other User role members to invite external users.',
    value: 'adminsGuestInvitersAndAllMembers',
  },
  ADMINS_AND_GUEST_INVITERS: {
    label:
      'Allow members of Global Administrators, User Administrators, and Guest Inviter roles to invite external users.',
    value: 'adminsAndGuestInviters',
  },
  NONE: {
    label: 'Prevent everyone, including admins, from inviting external users',
    value: 'none',
  },
})

const GUEST_USER_ACCESS = Object.freeze({
  // the labels need to match what we can receive from the GET request in compliance->settings
  // the values are what the backend expects to receive in the POST when labels are selected
  SAME_AS_MEMBER: {
    label: 'Guests have the same access to Azure AD resources as member users',
    roleId: 'a0b1b346-4d3e-4e8b-98f8-753987be4970',
  },
  LIMITED: {
    label: 'Guests can see membership of all non-hidden groups',
    roleId: '10dae51f-b6af-4016-8d66-8c2a99b929b3',
  },
  MOST_RESTRICTIVE: {
    label: "Guests can't see membership of any groups",
    roleId: '2af84b1e-32c8-42b7-82bc-daa82404023b',
  },
})

const DYNAMIC_GUEST_GROUP_TEMPLATE_NAME = 'CIS M365 V6 L1 - Ensure a dynamic group for guest users is created 5.1.3.1'
const DYNAMIC_GUEST_GROUP_TEMPLATE_TAG = 'CIS_M365_V6_L1_DYNAMIC_GUEST_GROUP_5_1_3_1'

const USER_OWNED_APPS_AND_SERVICES_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USER_OWNED_APPS_AND_SERVICES.CIS_M365_V6_L1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USER_OWNED_APPS_AND_SERVICES.CIS_M365_V6_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USER_OWNED_APPS_AND_SERVICES.CIS_V8.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USER_OWNED_APPS_AND_SERVICES.CIS_V8.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8_1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USER_OWNED_APPS_AND_SERVICES.CIS_V8.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USER_OWNED_APPS_AND_SERVICES.CIS_V8.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USEROWNEDAPPSANDSERVICESRESTRICTED.CMMC_L1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USEROWNEDAPPSANDSERVICESRESTRICTED.CMMC_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USEROWNEDAPPSANDSERVICESRESTRICTED.CMMC_L2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USEROWNEDAPPSANDSERVICESRESTRICTED.CMMC_L2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USEROWNEDAPPSANDSERVICESRESTRICTED.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USEROWNEDAPPSANDSERVICESRESTRICTED.HIPAA.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USEROWNEDAPPSANDSERVICESRESTRICTED.NIST_CSF_2_0.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USEROWNEDAPPSANDSERVICESRESTRICTED.NIST_CSF_2_0.DESCRIPTION',
  },
}

const FORMS_PHISHING_PROTECTION_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.FORMS_PHISHING_PROTECTION.CIS_M365_V6_L1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.FORMS_PHISHING_PROTECTION.CIS_M365_V6_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: [
    {
      controls: ['10.1'],
      titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.FORMS_PHISHING_PROTECTION.CIS_V8.10_1.TITLE',
      descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.FORMS_PHISHING_PROTECTION.CIS_V8.10_1.DESCRIPTION',
    },
    {
      controls: ['14.2'],
      titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.FORMS_PHISHING_PROTECTION.CIS_V8.14_2.TITLE',
      descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.FORMS_PHISHING_PROTECTION.CIS_V8.14_2.DESCRIPTION',
    },
  ],
  [COMPLIANCE_TYPES.CIS_V8_1]: [
    {
      controls: ['10.1'],
      titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.FORMS_PHISHING_PROTECTION.CIS_V8.10_1.TITLE',
      descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.FORMS_PHISHING_PROTECTION.CIS_V8.10_1.DESCRIPTION',
    },
    {
      controls: ['14.2'],
      titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.FORMS_PHISHING_PROTECTION.CIS_V8.14_2.TITLE',
      descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.FORMS_PHISHING_PROTECTION.CIS_V8.14_2.DESCRIPTION',
    },
  ],
}

const CIS_M365_V6_L2_2_1_7_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.2_1_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.2_1_7.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.2_1_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.2_1_7.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_2_1_6_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_6.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_6.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_6.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_6.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_2_1_15_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_15.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_15.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_15.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_15.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_2_1_1_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.2_1_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.2_1_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.2_1_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.2_1_1.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_2_1_4_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.2_1_4.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.2_1_4.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.2_1_4.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.2_1_4.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_2_1_5_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.2_1_5.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.2_1_5.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.2_1_5.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.2_1_5.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_2_1_2_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_2.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_6_2_1_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.6_2_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.6_2_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.6_2_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.6_2_1.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_6_2_3_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.6_2_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.6_2_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.6_2_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.6_2_3.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_2_3_1_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_3_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_3_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_3_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_3_1.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_2_4_1_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_4_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_4_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_4_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_4_1.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_2_1_8_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_8.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_8.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_8.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_8.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_2_1_9_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_9.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_9.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_9.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_9.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_2_1_10_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_10.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_10.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_10.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_10.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_3_1_1_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.3_1_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.3_1_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.3_1_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.3_1_1.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_1_1_1_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.1_1_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.1_1_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.1_1_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.1_1_1.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_1_1_3_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.1_1_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.1_1_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.1_1_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.1_1_3.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_1_3_1_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.1_3_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.1_3_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.1_3_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.1_3_1.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_2_2_2_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_2.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_2_2_3_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_3.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_2_2_4_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_4.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_4.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_4.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_4.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3655224.NIST_CSF_2_0.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3655224.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3655224.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3655224.HIPAA.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_2_2_12_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_12.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_12.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.16_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.16_7.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8_1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.16_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.16_7.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM36552212.CMMC_L1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM36552212.CMMC_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM36552212.CMMC_L2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM36552212.CMMC_L2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM36552212.NIST_CSF_2_0.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM36552212.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM36552212.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM36552212.HIPAA.DESCRIPTION',
  },
}

const INTUNE_ENROLLMENT_SIGN_IN_FREQUENCY_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INTUNE_ENROLLMENT.CIS_M365_V6_L1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INTUNE_ENROLLMENT.CIS_M365_V6_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INTUNE_ENROLLMENT.CIS_V8.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INTUNE_ENROLLMENT.CIS_V8.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8_1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INTUNE_ENROLLMENT.CIS_V8.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INTUNE_ENROLLMENT.CIS_V8.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INTUNEENROLLMENTSIGNINFREQUENCY.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INTUNEENROLLMENTSIGNINFREQUENCY.HIPAA.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INTUNEENROLLMENTSIGNINFREQUENCY.NIST_CSF_2_0.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INTUNEENROLLMENTSIGNINFREQUENCY.NIST_CSF_2_0.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_5_2_2_5_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.5_2_2_5.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.5_2_2_5.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.5_2_2_5.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.5_2_2_5.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_2_2_6_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_6.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_6.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_6.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_6.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_2_2_7_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_7.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_7.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_2_2_9_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_9.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_9.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_9.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_9.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_2_2_10_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_10.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_2_10.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_10.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_10.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_1_3_2_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.1_3_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.1_3_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.1_3_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.1_3_2.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_1_3_3_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.1_3_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.1_3_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.1_3_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.1_3_3.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_1_3_6_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.1_3_6.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.1_3_6.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.1_3_6.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.1_3_6.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM365136.NIST_CSF_2_0.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM365136.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM365136.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM365136.HIPAA.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_7_2_2_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.7_2_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.7_2_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTAZUREADB2BINTEGRATION.CMMC_L1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTAZUREADB2BINTEGRATION.CMMC_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTAZUREADB2BINTEGRATION.CMMC_L2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTAZUREADB2BINTEGRATION.CMMC_L2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTAZUREADB2BINTEGRATION.NIST_CSF_2_0.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTAZUREADB2BINTEGRATION.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTAZUREADB2BINTEGRATION.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTAZUREADB2BINTEGRATION.HIPAA.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_7_2_3_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.7_2_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.7_2_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.7_2_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.7_2_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_7_2_7_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.7_2_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.7_2_7.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.7_2_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.7_2_7.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDEFAULTSHARINGLINKTYPE.NIST_CSF_2_0.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDEFAULTSHARINGLINKTYPE.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDEFAULTSHARINGLINKTYPE.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDEFAULTSHARINGLINKTYPE.HIPAA.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_7_2_9_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.7_2_9.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.7_2_9.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.7_2_9.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.7_2_9.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTEXPIRATION.CMMC_L1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTEXPIRATION.CMMC_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTEXPIRATION.CMMC_L2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTEXPIRATION.CMMC_L2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTEXPIRATION.NIST_CSF_2_0.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTEXPIRATION.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTEXPIRATION.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTEXPIRATION.HIPAA.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_7_2_10_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.7_2_10.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.7_2_10.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTREAUTHENTICATION.CMMC_L1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTREAUTHENTICATION.CMMC_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTREAUTHENTICATION.CMMC_L2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTREAUTHENTICATION.CMMC_L2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTREAUTHENTICATION.NIST_CSF_2_0.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTREAUTHENTICATION.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTREAUTHENTICATION.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTGUESTREAUTHENTICATION.HIPAA.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_7_2_11_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.7_2_11.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.7_2_11.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.7_2_11.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.7_2_11.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDEFAULTLINKPERMISSION.CMMC_L2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDEFAULTLINKPERMISSION.CMMC_L2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDEFAULTLINKPERMISSION.NIST_CSF_2_0.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDEFAULTLINKPERMISSION.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDEFAULTLINKPERMISSION.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDEFAULTLINKPERMISSION.HIPAA.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_5_1_5_1_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.5_1_5_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.5_1_5_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.5_1_5_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.5_1_5_1.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_1_5_2_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_1_5_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_1_5_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_1_5_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_1_5_2.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_1_6_2_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_1_6_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_1_6_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_1_6_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_1_6_2.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_5_1_6_3_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.5_1_6_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.5_1_6_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.5_1_6_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.5_1_6_3.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_5_1_2_2_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.5_1_2_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.5_1_2_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_1_2_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_1_2_2.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_1_2_3_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_1_2_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_1_2_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_1_2_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_1_2_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3655123.NIST_CSF_2_0.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3655123.NIST_CSF_2_0.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_1_3_1_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_1_3_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_1_3_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8_1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_1_3_2_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_1_3_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_1_3_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_1_3_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_1_3_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3655132.NIST_CSF_2_0.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3655132.NIST_CSF_2_0.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_1_2_1_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_1_2_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_1_2_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_1_2_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_1_2_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.6_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.6_3.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_7_2_4_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.7_2_4.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.7_2_4.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.7_2_4.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.7_2_4.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ONEDRIVECONTENTSHARINGRESTRICTED.NIST_CSF_2_0.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ONEDRIVECONTENTSHARINGRESTRICTED.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ONEDRIVECONTENTSHARINGRESTRICTED.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ONEDRIVECONTENTSHARINGRESTRICTED.HIPAA.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_7_2_5_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.7_2_5.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.7_2_5.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.7_2_5.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.7_2_5.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_7_2_6_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.7_2_6.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.7_2_6.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.7_2_6.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.7_2_6.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDOMAINALLOWLIST.NIST_CSF_2_0.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDOMAINALLOWLIST.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDOMAINALLOWLIST.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDOMAINALLOWLIST.HIPAA.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_7_2_8_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.7_2_8.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.7_2_8.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.6_8.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.6_8.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8_1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.6_8.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.6_8.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALSHARINGSECURITYGROUP.CMMC_L1.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALSHARINGSECURITYGROUP.CMMC_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALSHARINGSECURITYGROUP.CMMC_L2.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALSHARINGSECURITYGROUP.CMMC_L2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALSHARINGSECURITYGROUP.NIST_CSF_2_0.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALSHARINGSECURITYGROUP.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALSHARINGSECURITYGROUP.HIPAA.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALSHARINGSECURITYGROUP.HIPAA.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_7_2_1_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.7_2_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.7_2_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.7_2_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.7_2_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_10.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.3_10.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_7_3_1_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.7_3_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.7_3_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.10_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.10_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8_1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.10_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.10_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDISALLOWINFECTEDFILEDOWNLOAD.CMMC_L1.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDISALLOWINFECTEDFILEDOWNLOAD.CMMC_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDISALLOWINFECTEDFILEDOWNLOAD.CMMC_L2.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDISALLOWINFECTEDFILEDOWNLOAD.CMMC_L2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDISALLOWINFECTEDFILEDOWNLOAD.NIST_CSF_2_0.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDISALLOWINFECTEDFILEDOWNLOAD.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDISALLOWINFECTEDFILEDOWNLOAD.HIPAA.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDISALLOWINFECTEDFILEDOWNLOAD.HIPAA.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_7_3_2_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.7_3_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.7_3_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTSYNCCLIENTRESTRICTION.NIST_CSF_2_0.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTSYNCCLIENTRESTRICTION.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTSYNCCLIENTRESTRICTION.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTSYNCCLIENTRESTRICTION.HIPAA.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_2_3_2_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_3_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_3_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_3_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_3_2.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_2_3_3_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_3_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_3_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_3_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_3_3.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_2_3_5_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_3_5.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_3_5.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_3_5.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_3_5.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_5_2_3_6_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_3_6.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.5_2_3_6.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_3_6.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_3_6.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.6_3.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.6_3.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3655236.NIST_CSF_2_0.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3655236.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3655236.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3655236.HIPAA.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_6_5_1_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.6_5_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.6_5_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.6_5_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.6_5_1.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_6_5_2_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.6_5_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.6_5_2.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_6_5_4_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.6_5_4.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.6_5_4.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.6_5_4.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.6_5_4.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_6_5_5_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.6_5_5.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.6_5_5.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.6_5_5.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.6_5_5.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_6_1_2_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.6_1_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.6_1_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.6_1_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.6_1_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.8_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.8_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM365612.CMMC_L2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM365612.CMMC_L2.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_6_2_2_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.6_2_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.6_2_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.6_2_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.6_2_2.DESCRIPTION',
  },
}

const CIS_M365_V6_L2_5_3_1_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.5_3_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.5_3_1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.5_3_1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L2.5_3_1.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_2_1_14_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_14.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_14.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_14.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_14.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_1_2_2_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.1_2_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.1_2_2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.1_2_2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.1_2_2.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_2_1_12_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_12.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_12.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_12.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_12.DESCRIPTION',
  },
}

const CIS_M365_V6_L1_2_1_13_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_13.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_13.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_13.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_13.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8_1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3652113.CMMC_L1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3652113.CMMC_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3652113.CMMC_L2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3652113.CMMC_L2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3652113.NIST_CSF_2_0.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3652113.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3652113.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM3652113.HIPAA.DESCRIPTION',
  },
}

const CONNECTION_FILTER_IP_ALLOW_LIST_COMPLIANCE_CONTENT = {
  ...CIS_M365_V6_L1_2_1_12_COMPLIANCE_CONTENT,
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8_1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTIONFILTERIPALLOWLIST.CMMC_L1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTIONFILTERIPALLOWLIST.CMMC_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTIONFILTERIPALLOWLIST.CMMC_L2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTIONFILTERIPALLOWLIST.CMMC_L2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTIONFILTERIPALLOWLIST.NIST_CSF_2_0.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTIONFILTERIPALLOWLIST.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTIONFILTERIPALLOWLIST.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTIONFILTERIPALLOWLIST.HIPAA.DESCRIPTION',
  },
}

const COMPREHENSIVE_ATTACHMENT_FILTERING_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.2_1_11.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L2.2_1_11.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_6.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_6.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8_1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_6.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_6.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.COMPREHENSIVEATTACHMENTFILTERING.CMMC_L1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.COMPREHENSIVEATTACHMENTFILTERING.CMMC_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.COMPREHENSIVEATTACHMENTFILTERING.CMMC_L2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.COMPREHENSIVEATTACHMENTFILTERING.CMMC_L2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.COMPREHENSIVEATTACHMENTFILTERING.NIST_CSF_2_0.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.COMPREHENSIVEATTACHMENTFILTERING.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.COMPREHENSIVEATTACHMENTFILTERING.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.COMPREHENSIVEATTACHMENTFILTERING.HIPAA.DESCRIPTION',
  },
}

const INBOUND_ANTI_SPAM_ALLOWED_DOMAINS_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_14.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_14.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_14.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_14.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8_1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INBOUNDANTISPAMALLOWEDDOMAINS.CMMC_L1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INBOUNDANTISPAMALLOWEDDOMAINS.CMMC_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INBOUNDANTISPAMALLOWEDDOMAINS.CMMC_L2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INBOUNDANTISPAMALLOWEDDOMAINS.CMMC_L2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INBOUNDANTISPAMALLOWEDDOMAINS.NIST_CSF_2_0.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INBOUNDANTISPAMALLOWEDDOMAINS.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INBOUNDANTISPAMALLOWEDDOMAINS.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INBOUNDANTISPAMALLOWEDDOMAINS.HIPAA.DESCRIPTION',
  },
}

const OUTBOUND_ANTI_SPAM_MESSAGE_LIMITS_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_15.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V6_L1.2_1_15.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_15.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.2_1_15.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CIS_V8_1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.9_7.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L1]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.OUTBOUNDANTISPAMMESSAGELIMITS.CMMC_L1.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.OUTBOUNDANTISPAMMESSAGELIMITS.CMMC_L1.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.CMMC_L2]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.OUTBOUNDANTISPAMMESSAGELIMITS.CMMC_L2.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.OUTBOUNDANTISPAMMESSAGELIMITS.CMMC_L2.DESCRIPTION',
  },
  [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.OUTBOUNDANTISPAMMESSAGELIMITS.NIST_CSF_2_0.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.OUTBOUNDANTISPAMMESSAGELIMITS.NIST_CSF_2_0.DESCRIPTION',
  },
  [COMPLIANCE_TYPES.HIPAA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.OUTBOUNDANTISPAMMESSAGELIMITS.HIPAA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.OUTBOUNDANTISPAMMESSAGELIMITS.HIPAA.DESCRIPTION',
  },
}

const AUTHENTICATION_METHODS_POLICY_CONFIGURE = {
  postureTemplate: true,
  checkModal: {
    complianceContent: CIS_M365_V6_L1_5_2_3_5_COMPLIANCE_CONTENT,
    customConfigure: 'authenticationMethodsPolicy',
  },
  properties: [
    {
      name: 'sms',
      title: 'SMS',
      statusOptions: [
        {
          label: 'Enabled',
          value: 'enabled',
        },
        {
          label: 'Disabled',
          value: 'disabled',
          bestPractice: true,
        },
      ],
    },
    {
      name: 'voiceCall',
      title: 'Voice call',
      statusOptions: [
        {
          label: 'Enabled',
          value: 'enabled',
        },
        {
          label: 'Disabled',
          value: 'disabled',
          bestPractice: true,
        },
      ],
    },
  ],
}

const SMART_LOCKOUT_THRESHOLD_PROPERTY = {
  name: 'lockoutThreshold',
  title: 'Lockout threshold',
  statusOptions: [
    {
      label: '10',
      value: 10,
      bestPractice: true,
    },
  ],
  hubTaskType: TASK_TYPES.passwordRuleSettings,
  hubAction: TASK_ACTIONS.update,
}

const SMART_LOCKOUT_DURATION_PROPERTY = {
  name: 'lockoutDurationInSeconds',
  title: 'Lockout duration (seconds)',
  statusOptions: [
    {
      label: '60',
      value: 60,
      bestPractice: true,
    },
  ],
  hubTaskType: TASK_TYPES.passwordRuleSettings,
  hubAction: TASK_ACTIONS.update,
}

const SMART_LOCKOUT_CONFIGURE = {
  integrationType: 'Office365',
  mgmTaskId: 'M365_SECURE_PARTIAL',
  taskName: 'M365_SMART_LOCKOUT',
  checkModal: {
    customConfigure: 'smartLockout',
  },
  properties: [SMART_LOCKOUT_THRESHOLD_PROPERTY, SMART_LOCKOUT_DURATION_PROPERTY],
}

// Shared mailbox display names carry this suffix in the tenant data; the check modal
// section title already says "shared mailboxes", so drop it to keep the column compact
const SHARED_MAILBOX_NAME_SUFFIX = /\s*\(shared mailbox\)\s*$/i

// Shared check modal details columns for user/account entity lists.
// cellType values map to the standard renderers in the check modal EntityList.
// Sort order for humanized last-active values: Today (0), 'Nd ago' (N), Never (largest)
const LAST_ACTIVE_NEVER_SORT_VALUE = Number.MAX_SAFE_INTEGER
const getLastActiveSortValue = (row) => {
  if (row.lastActive === 'Never' || row.lastActive == null) {
    return LAST_ACTIVE_NEVER_SORT_VALUE
  }
  if (row.lastActive === 'Today') {
    return 0
  }
  return parseInt(row.lastActive, 10) || 0
}

const USER_ENTITY_COLUMNS = {
  USER: {
    dataField: 'displayName',
    textId: 'GENERAL.USER',
    cellType: 'employeeName',
    sortable: true,
    searchable: true,
  },
  ROLES: {
    dataField: 'role',
    textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.ROLES',
    getValue: (row) => row.role?.join(', '),
    filterType: 'multiselect',
  },
  MFA_CONFIGURATION: {
    dataField: 'mfaConfigurations',
    textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.MFA_CONFIGURATION',
    cellType: 'mfaConfiguration',
    filterType: 'multiselect',
    getFilterValue: (row, intl) => {
      const status = row.mfaConfigurations?.configurationStatus
      return status ? intl.formatMessage({ id: `INTEGRATION.${status}` }) : ''
    },
  },
  MFA_STATUS: {
    dataField: 'mfaStatus',
    textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.MFA_STATUS',
    cellType: 'mfaStatus',
    auditSignal: true,
    filterType: 'multiselect',
    getFilterValue: (row, intl) => {
      const status = row.mfaStatus?.status
      return status ? intl.formatMessage({ id: `INTEGRATION.${status}` }) : ''
    },
    getDefaultFilterValue: (intl) => [intl.formatMessage({ id: 'INTEGRATION.NOT_PROTECTED' })],
  },
}

// Renders a per-entity boolean as a localized allowed/blocked label. allowedWhen maps
// the field's boolean to the "Allowed" label; the inverse renders "Blocked".
const allowBlockStatus = (field, allowedWhen) => (row, intl) =>
  intl.formatMessage({
    id:
      row[field] === allowedWhen ? 'COMPLIANCE_AUDIT.CHECK.DETAILS.ALLOWED' : 'COMPLIANCE_AUDIT.CHECK.DETAILS.BLOCKED',
  })

const buildSystemPreferredMfaTarget = (state = 'enabled') => ({
  state,
  includeTargets: [{ targetType: 'group', id: 'all_users' }],
  excludeTargets: [],
})

// Expands one per-check Essential Eight title/description pair across all three
// maturity-level framework keys (L1/L2/L3). The copy is intentionally identical across
// levels — the level distinction is conveyed by the card's framework name and the
// per-level ISM controls from postureConfig. Emitting all three is safe regardless of
// which levels a check covers: the Overview builds cards from postureConfig.compliance and
// only looks up content for the levels present there (getContent does a keyed lookup, not
// an enumeration), so entries for levels a check does not map to are never read. Keys
// follow the per-check convention: COMPLIANCE.<CHECK>.ESSENTIAL_8.<TITLE|DESCRIPTION>.
const essential8Content = (titleId, descriptionId) => ({
  [COMPLIANCE_TYPES.ESSENTIAL_8_L1]: { titleId, descriptionId },
  [COMPLIANCE_TYPES.ESSENTIAL_8_L2]: { titleId, descriptionId },
  [COMPLIANCE_TYPES.ESSENTIAL_8_L3]: { titleId, descriptionId },
})

const CISA_BLOCKLEGACYAUTHENTICATION_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKLEGACYAUTHENTICATION.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKLEGACYAUTHENTICATION.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD21_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD21.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD21.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD23_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD23.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD23.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD31_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD31.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD31.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_ADMINMFA_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_USERMFA_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USERMFA.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USERMFA.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD33_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD33.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD33.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD35_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD35.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD35.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD36_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD36.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD36.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD37_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD37.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD37.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD38_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD38.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD38.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD51_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD51.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD51.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_EIDSCAAP10_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAAP10.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAAP10.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CONNECTEDAPPSADMINCONSENT_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTEDAPPSADMINCONSENT.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTEDAPPSADMINCONSENT.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_EIDSCAAP08_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAAP08.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAAP08.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD53_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD53.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD53.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD61_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD61.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD61.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD71_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD71.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD71.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD72_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD72.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD72.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD73_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD73.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD73.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD74_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD74.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD74.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD75_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD75.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD75.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD76_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD76.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD76.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD81_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD81.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD81.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_EIDSCAAP07_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAAP07.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAAP07.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_ENTRAIDGUESTUSERACCESSPERMISSIONS_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENTRAIDGUESTUSERACCESSPERMISSIONS.CISA_SCUBA.TITLE',
    descriptionId:
      'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENTRAIDGUESTUSERACCESSPERMISSIONS.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSAAD82_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD82.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD82.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_SHAREPOINTEXTERNALGUESTSHARING_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALGUESTSHARING.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALGUESTSHARING.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_EXTERNALMAILFORWARDING_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EXTERNALMAILFORWARDING.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EXTERNALMAILFORWARDING.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_SAFEATTACHMENT_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SAFEATTACHMENT.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SAFEATTACHMENT.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_UNIFIEDAUDITLOG_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.UNIFIEDAUDITLOG.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.UNIFIEDAUDITLOG.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_MAILBOXAUDIT_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MAILBOXAUDIT.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MAILBOXAUDIT.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSEXO22_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO22.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO22.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_DKIM_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.DKIM.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.DKIM.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSEXO41_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO41.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO41.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSEXO42_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO42.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO42.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSEXO43_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO43.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO43.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSEXO51_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO51.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO51.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSEXO61_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO61.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO61.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSEXO62_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO62.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO62.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_EXTERNALMAILTAG_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EXTERNALMAILTAG.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EXTERNALMAILTAG.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_CISAMSEXO71_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO71.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSEXO71.CISA_SCUBA.DESCRIPTION',
  },
}

const CISA_TEAMSDEFAULTEXTERNALOPTIONS_COMPLIANCE_CONTENT = {
  [COMPLIANCE_TYPES.CISA_SCUBA]: {
    titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.TEAMSDEFAULTEXTERNALOPTIONS.CISA_SCUBA.TITLE',
    descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.TEAMSDEFAULTEXTERNALOPTIONS.CISA_SCUBA.DESCRIPTION',
  },
}

const preTaskDefinitions = {
  M365_MS_AAD_7_2: {
    checkName: 'CISA.MS.AAD.7.2',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...CISA_CISAMSAAD72_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD72.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD72.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_MS_EXO_6_1: {
    checkName: 'CISA.MS.EXO.6.1',
    portal: [PORTALS.EXCHANGE],
    checkModal: { complianceContent: CISA_CISAMSEXO61_COMPLIANCE_CONTENT },
  },
  M365_TEAMS_DEFAULT_EXTERNAL_OPTIONS: {
    checkName: 'teamsdefaultexternaloptions',
    portal: [PORTALS.TEAMS],
    checkModal: {
      complianceContent: {
        ...CISA_TEAMSDEFAULTEXTERNALOPTIONS_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.TEAMSDEFAULTEXTERNALOPTIONS.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.TEAMSDEFAULTEXTERNALOPTIONS.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.TEAMSDEFAULTEXTERNALOPTIONS.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.TEAMSDEFAULTEXTERNALOPTIONS.CMMC_L2.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.TEAMSDEFAULTEXTERNALOPTIONS.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.TEAMSDEFAULTEXTERNALOPTIONS.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_BASE_DEFAULTS: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
  },
  M365_GENERIC_COMPLIANT: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    properties: [
      {
        name: 'compliant',
      },
    ],
  },
  M365_PASSWORD_RESET_NOTIFICATION: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_PASSWORD_RESET_NOTIFICATION',
    properties: [
      {
        name: 'passwordResetNotification',
        defaultValue: false,
      },
    ],
  },
  M365_ADMIN_PASSWORD_RESET_NOTIFICATION: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_ADMIN_PASSWORD_RESET_NOTIFICATION',
    properties: [
      {
        name: 'adminPasswordResetNotification',
        defaultValue: false,
      },
    ],
  },
  M365_UNIFIED_AUDIT_LOG: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_UNIFIED_AUDIT_LOG',
    checkName: 'unifiedauditlog',
    portal: [PORTALS.PURVIEW],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_3_1_1_COMPLIANCE_CONTENT,
        ...essential8Content(
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.UNIFIEDAUDITLOG.ESSENTIAL_8.TITLE',
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.UNIFIEDAUDITLOG.ESSENTIAL_8.DESCRIPTION',
        ),
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.UNIFIEDAUDITLOG.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.UNIFIEDAUDITLOG.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.UNIFIEDAUDITLOG.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.UNIFIEDAUDITLOG.NIST_CSF_2_0.DESCRIPTION',
        },
        ...CISA_UNIFIEDAUDITLOG_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.UNIFIEDAUDITLOG.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.UNIFIEDAUDITLOG.CMMC_L2.DESCRIPTION',
        },
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'unifiedAuditLog',
        title: 'Unified Audit Log',
        statusOptions: [
          {
            label: 'On',
            value: true,
            bestPractice: true,
          },
          {
            label: 'Off',
            value: false,
          },
        ],
      },
    ],
    baseline: {
      unifiedAuditLog: true,
    },
  },
  CONNECTED_APPS_ADMIN_CONSENT: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_CONNECTED_APPS_AND_APPS_CONSENT',
    checkName: 'connectedappsadminconsent',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L2_5_1_5_1_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTEDAPPSADMINCONSENT.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTEDAPPSADMINCONSENT.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTEDAPPSADMINCONSENT.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTEDAPPSADMINCONSENT.NIST_CSF_2_0.DESCRIPTION',
        },
        ...CISA_CONNECTEDAPPSADMINCONSENT_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTEDAPPSADMINCONSENT.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTEDAPPSADMINCONSENT.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTEDAPPSADMINCONSENT.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CONNECTEDAPPSADMINCONSENT.CMMC_L2.DESCRIPTION',
        },
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'connectedAppsAndAdminConsent',
        title: 'Disable User Application Consent',
        defaultValue: false,
        statusOptions: [
          {
            label: 'On',
            value: false,
            bestPractice: true,
          },
          {
            label: 'Off',
            value: true,
          },
        ],
      },
    ],
    baseline: {
      connectedAppsAndAdminConsent: false,
    },
  },
  M365_ENABLE_USER_MFA: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_ENABLE_USER_MFA',
    properties: [
      {
        name: 'employeeGuids',
        defaultValue: [],
      },
      {
        name: 'mfaType',
        defaultValue: 'none',
      },
    ],
  },
  M365_DISABLE_USER_MFA: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_DISABLE_USER_MFA',
    properties: [
      {
        name: 'employeeGuids',
        defaultValue: [],
      },
      {
        name: 'mfaType',
        defaultValue: 'none',
      },
    ],
  },
  M365_DELETE_USER: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_DELETE_USER',
    properties: [
      {
        name: 'employeeIdentifier',
        defaultValue: null,
      },
      {
        name: 'revokeSessions',
        defaultValue: null,
      },
      {
        name: 'disableUser',
        defaultValue: null,
      },
      {
        name: 'resetPassword',
        defaultValue: null,
      },
      {
        name: 'deleteUser',
        defaultValue: null,
      },
      {
        name: 'emailAliasesToBeRemoved',
        defaultValue: null,
      },
      {
        name: 'removeDelegatePermissions',
        defaultValue: null,
      },
      {
        name: 'oneDriveUsers',
        defaultValue: null,
      },
      {
        name: 'forwardMailboxTo',
        defaultValue: null,
      },
      {
        name: 'oneDriveLocation',
        defaultValue: null,
      },
      {
        name: 'sharedMailboxConfig',
        defaultValue: null,
      },
    ],
  },
  M365_REVOKE_SESSIONS: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_REVOKE_SESSIONS',
    properties: [
      {
        name: 'employeeIdentifier',
        defaultValue: null,
      },
    ],
  },
  M365_PWD_RESET: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_PWD_RESET',
    properties: [
      {
        name: 'employeeEmail',
        defaultValue: null,
      },
      {
        name: 'employeeIdentifier',
        defaultValue: null,
      },
      {
        name: 'passwordInfo',
        defaultValue: {},
      },
    ],
  },
  M365_ADD_USER: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_ADD_USER',
    properties: [
      {
        name: 'firstName',
        defaultValue: null,
      },
      {
        name: 'lastName',
        defaultValue: null,
      },
      {
        name: 'employeeIdentifier',
        defaultValue: null,
      },
      {
        name: 'displayName',
        defaultValue: null,
      },
      {
        name: 'email',
        defaultValue: null,
      },
      {
        name: 'department',
        defaultValue: null,
      },
      {
        name: 'jobTitle',
        defaultValue: null,
      },
      {
        name: 'passwordInfo',
        defaultValue: [
          {
            name: 'newPassword',
            defaultValue: null,
          },
          {
            name: 'sendPasswordTo',
            defaultValue: null,
          },
        ],
      },
      {
        name: 'licenseInfo',
        defaultValue: [
          {
            name: 'usageLocation',
            defaultValue: undefined,
          },
          {
            name: 'toBeAdded',
            defaultValue: [],
          },
          {
            name: 'usageLocation',
            toBeRemoved: [],
          },
        ],
      },
    ],
  },
  M365_MAILBOX_AUDITING: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_MAILBOX_AUDITING',
    checkName: 'mailboxaudit',
    portal: [PORTALS.EXCHANGE],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_6_1_2_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MAILBOXAUDIT.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MAILBOXAUDIT.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MAILBOXAUDIT.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MAILBOXAUDIT.NIST_CSF_2_0.DESCRIPTION',
        },
        ...CISA_MAILBOXAUDIT_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MAILBOXAUDIT.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MAILBOXAUDIT.CMMC_L2.DESCRIPTION',
        },
      },
    },
    postureTemplate: true,
    properties: [
      {
        // the name needs to match what backend is expecting in actions (see POST request for reference)
        name: 'mailboxAuditingDisabled',
        // the title needs to match what we receive from the GET request in compliance->settings
        title: 'Mailbox Auditing (organization level)',
        // the status options are all options that backend accepts for this property
        statusOptions: [
          {
            // the label needs to match what we can receive from the GET request in compliance->settings
            label: 'Enabled',
            // the value needs is what the backend expects to receive in the POST if this label is selected
            value: false,
            bestPractice: true,
          },
          {
            label: 'Disabled',
            value: true,
          },
        ],
      },
    ],
    baseline: {
      mailboxAuditingDisabled: false,
    },
  },
  M365_EXTERNAL_MESSAGE_TAG: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_EXTERNAL_MESSAGE_TAG',
    checkName: 'externalMailTag',
    portal: [PORTALS.EXCHANGE],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_6_2_3_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EXTERNALMAILTAG.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EXTERNALMAILTAG.NIST_CSF_2_0.DESCRIPTION',
        },
        ...CISA_EXTERNALMAILTAG_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EXTERNALMAILTAG.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EXTERNALMAILTAG.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EXTERNALMAILTAG.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EXTERNALMAILTAG.CMMC_L2.DESCRIPTION',
        },
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'externalMessageTagEnabled',
        // the title needs to match what we receive from the GET request in compliance->settings
        title: 'External Tagging in Exchange Online',
        statusOptions: [
          {
            // the label needs to match what we can receive from the GET request in compliance->settings
            label: 'Disabled',
            // the value needs is what the backend expects to receive in the POST if this label is selected
            value: false,
          },
          {
            label: 'Enabled',
            value: true,
            bestPractice: true,
          },
        ],
      },
    ],
    baseline: {
      externalMessageTagEnabled: true,
    },
  },
  M365_MANAGE_POWERSHELL_ACCESS: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_MANAGE_POWERSHELL_ACCESS',
    checkName: 'powershellaccessfornonadmins',
    portal: [PORTALS.EXCHANGE],
    checkModal: {
      customConfigure: 'powershellAccessForNonAdmins',
      complianceContent: {
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.POWERSHELLACCESSFORNONADMINS.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.POWERSHELLACCESSFORNONADMINS.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.POWERSHELLACCESSFORNONADMINS.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.POWERSHELLACCESSFORNONADMINS.CMMC_L2.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.POWERSHELLACCESSFORNONADMINS.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.POWERSHELLACCESSFORNONADMINS.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.POWERSHELLACCESSFORNONADMINS.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.POWERSHELLACCESSFORNONADMINS.HIPAA.DESCRIPTION',
        },
      },
      configure: {
        entities: {
          keyField: 'userId',
          labelId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.USER_ACCOUNTS',
          columns: [
            USER_ENTITY_COLUMNS.USER,
            {
              dataField: 'remotePowerShellEnabled',
              textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.REMOTE_POWERSHELL',
              // non-admins with remote PowerShell enabled are non-compliant
              getValue: allowBlockStatus('remotePowerShellEnabled', true),
              auditSignal: true,
            },
          ],
        },
      },
    },
    properties: [
      {
        name: 'remotePowerShellEnabled',
        defaultValue: false,
      },
      {
        name: 'employeeGuids',
        defaultValue: [],
      },
    ],
  },
  M365_MANAGE_DKIM_SIGNING: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_MANAGE_DKIM_SIGNING',
    checkName: 'dkim',
    portal: [PORTALS.DEFENDER, PORTALS.DNS_EXTERNAL],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_2_1_9_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.DKIM.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.DKIM.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.DKIM.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.DKIM.NIST_CSF_2_0.DESCRIPTION',
        },
        ...CISA_DKIM_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.DKIM.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.DKIM.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.DKIM.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.DKIM.CMMC_L2.DESCRIPTION',
        },
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'overallDKIMStatus',
        // the title needs to match what we receive from the GET request in compliance->settings
        title: 'DKIM',
        statusOptions: [
          {
            // the label needs to match what we can receive from the GET request in compliance->settings
            label: 'Disabled',
            // the value needs is what the backend expects to receive in the POST if this label is selected
            value: false,
          },
          {
            label: 'Enabled',
            value: true,
            bestPractice: true,
          },
        ],
      },
    ],
  },
  M365_MANAGE_CONTACT_SETTINGS: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_MANAGE_CONTACT_SETTINGS',
    properties: [
      {
        name: 'marketingNotificationEmail',
        defaultValue: null,
      },
      {
        name: 'securityComplianceNotificationMail',
        defaultValue: null,
      },
      {
        name: 'technicalNotificationMail',
        defaultValue: null,
      },
      {
        name: 'contactEmail',
        defaultValue: null,
      },
    ],
  },
  M365_MANAGE_RESHARING_ACCESS: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_MANAGE_RESHARING_ACCESS',
    checkName: 'resharingbyexternalusers',
    portal: [PORTALS.SHAREPOINT],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L2_7_2_5_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.RESHARINGBYEXTERNALUSERS.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.RESHARINGBYEXTERNALUSERS.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.RESHARINGBYEXTERNALUSERS.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.RESHARINGBYEXTERNALUSERS.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.RESHARINGBYEXTERNALUSERS.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.RESHARINGBYEXTERNALUSERS.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.RESHARINGBYEXTERNALUSERS.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.RESHARINGBYEXTERNALUSERS.CMMC_L2.DESCRIPTION',
        },
      },
      customConfigure: 'spoSharingSettings',
    },
    postureTemplate: true,
    properties: [
      {
        name: 'isResharingByExternalUsersEnabled',
        // the title needs to match what we receive from the GET request in compliance->settings
        title: 'Resharing By External Users',
        statusOptions: [
          {
            // the label needs to match what we can receive from the GET request in compliance->settings
            label: 'Disabled',
            // the value needs is what the backend expects to receive in the POST if this label is selected
            value: false,
            bestPractice: true,
          },
          {
            label: 'Enabled',
            value: true,
          },
        ],
        hubTaskType: TASK_TYPES.sharepointSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
  },
  M365_SITE_CREATION_BY_STANDARD_USERS: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_SITE_CREATION_BY_STANDARD_USERS',
    checkName: 'sitecreationforstandardusers',
    portal: [PORTALS.SHAREPOINT],
    checkModal: {
      complianceContent: {
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SITECREATIONFORSTANDARDUSERS.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SITECREATIONFORSTANDARDUSERS.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SITECREATIONFORSTANDARDUSERS.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SITECREATIONFORSTANDARDUSERS.CMMC_L2.DESCRIPTION',
        },
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'isSiteCreationEnabled',
        mirrorProperty: 'isSiteCreationUIEnabled',
        // the title needs to match what we receive from the GET request in compliance->settings
        title: 'Only allow admins to create Sharepoint sites',
        statusOptions: [
          {
            // the label needs to match what we can receive from the GET request in compliance->settings
            label: 'Enabled',
            // the value needs is what the backend expects to receive in the POST if this label is selected
            value: false,
            bestPractice: true,
          },
          {
            label: 'Disabled',
            value: true,
          },
        ],
        hubTaskType: TASK_TYPES.sharepointSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
  },
  M365_RETAIN_DELETED_USER_FOR_ONE_YEAR: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_RETAIN_DELETED_USER_FOR_ONE_YEAR',
    checkName: 'deleteduseronedriveretention',
    portal: [PORTALS.SHAREPOINT],
    checkModal: {
      complianceContent: {
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.DELETEDUSERONEDRIVERETENTION.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.DELETEDUSERONEDRIVERETENTION.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'deletedUserPersonalSiteRetentionPeriodInDays',
        // the title needs to match what we receive from the GET request in compliance->settings
        title: "Retain deleted user's OneDrive for",
        statusOptions: [
          {
            // the label needs to match what we can receive from the GET request in compliance->settings
            label: '30 Days (Default value)',
            // the value needs is what the backend expects to receive in the POST if this label is selected
            value: 30,
          },
          {
            label: '93 Days (after which Microsoft charges for storage)',
            value: 93,
          },
          {
            label: '180 Days',
            value: 180,
          },
          {
            label: '365 Days',
            value: 365,
          },
        ],
        hubTaskType: TASK_TYPES.sharepointSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
  },
  M365_MANAGE_AUTHENTICATOR_NUMBER_MACHING: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_MANAGE_AUTHENTICATOR_NUMBER_MACHING',
    checkName: 'mfanumbermatching',
    portal: [PORTALS.ENTRA],
    checkModal: {
      configure: {
        alertMessageId: 'SECURE.MFA_NUMBER_MATCHING_WARNING',
      },
      complianceContent: {
        ...CIS_M365_V6_L1_5_2_3_1_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MFANUMBERMATCHING.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MFANUMBERMATCHING.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MFANUMBERMATCHING.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MFANUMBERMATCHING.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MFANUMBERMATCHING.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MFANUMBERMATCHING.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MFANUMBERMATCHING.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MFANUMBERMATCHING.CMMC_L2.DESCRIPTION',
        },
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'microsoftAuthenticatorMethodState',
        title: 'Microsoft Authenticator',
        htmlId: 'save-microsoft-authenticator-method-state',
        statusOptions: [
          {
            label: 'Disabled',
            value: 'disabled',
          },
          {
            label: 'Enabled',
            value: 'enabled',
            bestPractice: true,
          },
        ],
      },
      {
        name: 'numberMatchingRequiredState',
        title: 'Number Matching',
        htmlId: 'save-number-matching-required-state',
        isDisabled: true,
        statusOptions: [
          {
            label: 'Disabled',
            value: 'disabled',
          },
          {
            label: 'Enabled',
            value: 'enabled',
            bestPractice: true,
          },
        ],
      },
    ],
  },
  M365_MANAGE_AUTHENTICATOR_APPLICATION_NAME: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_MANAGE_AUTHENTICATOR_APPLICATION_NAME',
    checkName: 'EIDSCA.AM06',
    portal: [PORTALS.ENTRA],
    checkModal: { complianceContent: CIS_M365_V6_L1_5_2_3_1_COMPLIANCE_CONTENT },
    postureTemplate: true,
    properties: [
      {
        name: 'displayAppInformationRequiredState',
        title: 'Show Application Name',
        hubTaskType: TASK_TYPES.authenticatorApplicationName,
        hubAction: TASK_ACTIONS.update,
        statusOptions: [
          {
            label: 'Disabled',
            value: 'disabled',
          },
          {
            label: 'Enabled',
            value: 'enabled',
            bestPractice: true,
          },
          {
            label: 'Microsoft Managed',
            value: 'default',
          },
        ],
      },
    ],
  },
  M365_AUTHENTICATOR_DISPLAY_LOCATION: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_AUTHENTICATOR_DISPLAY_LOCATION',
    checkName: 'EIDSCA.AM09',
    portal: [PORTALS.ENTRA],
    checkModal: { complianceContent: CIS_M365_V6_L1_5_2_3_1_COMPLIANCE_CONTENT },
    postureTemplate: true,
    properties: [
      {
        name: 'displayLocationInformationRequiredState',
        title: 'Show Geographic Location',
        statusOptions: [
          {
            label: 'Disabled',
            value: 'disabled',
          },
          {
            label: 'Enabled',
            value: 'enabled',
            bestPractice: true,
          },
          {
            label: 'Microsoft Managed',
            value: 'default',
          },
        ],
        hubTaskType: TASK_TYPES.authenticatorDisplayLocation,
        hubAction: TASK_ACTIONS.update,
      },
    ],
  },
  M365_ADMIN_CONSENT_WORKFLOW: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_ADMIN_CONSENT_WORKFLOW',
    checkName: 'CISA.MS.AAD.5.3',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: { ...CIS_M365_V6_L1_5_1_5_2_COMPLIANCE_CONTENT, ...CISA_CISAMSAAD53_COMPLIANCE_CONTENT },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'isEnabled',
        title: 'Admin consent requests',
        statusOptions: [
          {
            label: 'Disabled',
            value: false,
          },
          {
            label: 'Enabled',
            value: true,
            bestPractice: true,
          },
        ],
        hubTaskType: TASK_TYPES.adminConsentWorkflow,
        hubAction: TASK_ACTIONS.update,
      },
      {
        name: 'notifyReviewers',
        title: 'Reviewer email notifications',
        statusOptions: [
          {
            label: 'Disabled',
            value: false,
          },
          {
            label: 'Enabled',
            value: true,
            bestPractice: true,
          },
        ],
        hubTaskType: TASK_TYPES.adminConsentWorkflow,
        hubAction: TASK_ACTIONS.update,
      },
      {
        name: 'remindersEnabled',
        title: 'Reviewer expiration reminders',
        statusOptions: [
          {
            label: 'Disabled',
            value: false,
          },
          {
            label: 'Enabled',
            value: true,
            bestPractice: true,
          },
        ],
        hubTaskType: TASK_TYPES.adminConsentWorkflow,
        hubAction: TASK_ACTIONS.update,
      },
      {
        name: 'requestDurationInDays',
        title: 'Consent request expires after (days)',
        statusOptions: [
          {
            label: '7 days',
            value: 7,
          },
          {
            label: '14 days',
            value: 14,
          },
          {
            label: '30 days',
            value: 30,
            bestPractice: true,
          },
          {
            label: '60 days',
            value: 60,
          },
          {
            label: '90 days',
            value: 90,
          },
        ],
        hubTaskType: TASK_TYPES.adminConsentWorkflow,
        hubAction: TASK_ACTIONS.update,
      },
    ],
  },
  M365_BANNED_PASSWORD_LIST: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_BANNED_PASSWORD_LIST',
    checkName: 'EIDSCA.PR03',
    portal: [PORTALS.ENTRA],
    postureTemplate: true,
    checkModal: {
      customConfigure: 'bannedPasswordList',
    },
    properties: [
      {
        name: 'isEnabled',
        title: 'Enforce custom list',
        statusOptions: [
          {
            label: 'Disabled',
            value: false,
          },
          {
            label: 'Enabled',
            value: true,
            bestPractice: true,
          },
        ],
        hubTaskType: TASK_TYPES.passwordRuleSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    baseline: {
      isEnabled: true,
    },
  },
  M365_SMART_LOCKOUT: {
    ...SMART_LOCKOUT_CONFIGURE,
    baseline: {
      lockoutThreshold: 10,
      lockoutDurationInSeconds: 60,
    },
  },
  M365_APPLICATION_REGISTRATION_POLICY: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_APPLICATION_REGISTRATION_POLICY',
    checkName: 'CISA.MS.AAD.5.1',
    portal: [PORTALS.ENTRA],
    checkModal: {
      configure: {
        alertMessageId: APPLICATION_REGISTRATION_POLICY_WARNING_MESSAGE_ID,
      },
      complianceContent: { ...CIS_M365_V6_L2_5_1_2_2_COMPLIANCE_CONTENT, ...CISA_CISAMSAAD51_COMPLIANCE_CONTENT },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'allowedToCreateApps',
        title: 'Users can register applications',
        statusOptions: getApplicationRegistrationStatusOptions,
        hubTaskType: TASK_TYPES.applicationRegistrationPolicy,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    baseline: {
      allowedToCreateApps: false,
    },
  },
  M365_TENANT_CREATION_POLICY: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_TENANT_CREATION_POLICY',
    checkName: 'AUG.CIS.M365.5.1.2.3',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: CIS_M365_V6_L1_5_1_2_3_COMPLIANCE_CONTENT,
    },
    postureTemplate: true,
    properties: [
      {
        name: 'allowedToCreateTenants',
        title: 'Restrict non-admin users from creating tenants',
        statusOptions: getTenantCreationStatusOptions,
        hubTaskType: TASK_TYPES.tenantCreationPolicy,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    baseline: {
      allowedToCreateTenants: false,
    },
  },
  M365_MANAGE_DOMAIN_PASSWORD_VALIDITY: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_MANAGE_DOMAIN_PASSWORD_VALIDITY',
    checkName: 'CISA.MS.AAD.6.1',
    portal: [PORTALS.M365_ADMIN_CENTER],
    postureTemplate: true,
    checkModal: {
      customConfigure: 'passwordsShouldNotExpire',
      complianceContent: { ...CIS_M365_V6_L1_1_3_1_COMPLIANCE_CONTENT, ...CISA_CISAMSAAD61_COMPLIANCE_CONTENT },
    },
    properties: [
      {
        name: 'passwordValidityPeriodInDays',
        title: 'Passwords should not expire',
        statusOptions: [
          {
            label: 'Never expires',
            value: 2147483647,
            bestPractice: true,
          },
          {
            label: `Expires after 90 days (default)`,
            value: 90,
          },
        ],
        hubTaskType: TASK_TYPES.domains,
        hubAction: TASK_ACTIONS.update,
      },
    ],
  },
  M365_MODERN_AUTHENTICATION_EXO: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_MODERN_AUTHENTICATION_EXO',
    checkName: 'enableexchangemodernauth',
    portal: [PORTALS.EXCHANGE],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_6_5_1_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENABLEEXCHANGEMODERNAUTH.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENABLEEXCHANGEMODERNAUTH.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENABLEEXCHANGEMODERNAUTH.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENABLEEXCHANGEMODERNAUTH.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENABLEEXCHANGEMODERNAUTH.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENABLEEXCHANGEMODERNAUTH.CMMC_L2.DESCRIPTION',
        },
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'oAuth2ClientProfileEnabled',
        // the title needs to match what we receive from the GET request in compliance->settings
        title: 'Modern Authentication for Exchange/Outlook',
        statusOptions: [
          {
            // the label needs to match what we can receive from the GET request in compliance->settings
            label: 'Enabled',
            // the value needs is what the backend expects to receive in the POST if this label is selected
            value: true,
            bestPractice: true,
          },
          {
            label: 'Disabled',
            value: false,
          },
        ],
      },
    ],
  },
  M365_AZURE_AD_GUEST_ACCESS: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_AZURE_AD_GUEST_ACCESS',
    checkName: 'entraIdGuestUserAccessPermissions',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_5_1_6_2_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENTRAIDGUESTUSERACCESSPERMISSIONS.HIPAA.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENTRAIDGUESTUSERACCESSPERMISSIONS.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENTRAIDGUESTUSERACCESSPERMISSIONS.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENTRAIDGUESTUSERACCESSPERMISSIONS.NIST_CSF_2_0.DESCRIPTION',
        },
        ...CISA_ENTRAIDGUESTUSERACCESSPERMISSIONS_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENTRAIDGUESTUSERACCESSPERMISSIONS.CMMC_L1.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENTRAIDGUESTUSERACCESSPERMISSIONS.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENTRAIDGUESTUSERACCESSPERMISSIONS.CMMC_L2.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ENTRAIDGUESTUSERACCESSPERMISSIONS.CMMC_L2.DESCRIPTION',
        },
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'authorizationPolicyGuestUserRoleId',
        // the title needs to match what we receive from the GET request in compliance->settings
        title: 'Guest user access',
        statusOptions: [
          {
            label: GUEST_USER_ACCESS.SAME_AS_MEMBER.label,
            value: GUEST_USER_ACCESS.SAME_AS_MEMBER.roleId,
          },
          {
            label: GUEST_USER_ACCESS.LIMITED.label,
            value: GUEST_USER_ACCESS.LIMITED.roleId,
          },
          {
            label: GUEST_USER_ACCESS.MOST_RESTRICTIVE.label,
            value: GUEST_USER_ACCESS.MOST_RESTRICTIVE.roleId,
            bestPractice: true,
          },
        ],
      },
      {
        name: 'authorizationPolicyAllowInvitesFrom',
        title: 'Guest user invite settings',
        statusOptions: [
          {
            label: GUEST_USER_INVITATIONS.NONE.label,
            value: GUEST_USER_INVITATIONS.NONE.value,
          },
          {
            label: GUEST_USER_INVITATIONS.ADMINS_AND_GUEST_INVITERS.label,
            value: GUEST_USER_INVITATIONS.ADMINS_AND_GUEST_INVITERS.value,
            bestPractice: true,
          },
          {
            label: GUEST_USER_INVITATIONS.ADMINS_GUEST_INVITERS_AND_ALL_MEMBERS.label,
            value: GUEST_USER_INVITATIONS.ADMINS_GUEST_INVITERS_AND_ALL_MEMBERS.value,
          },
          {
            label: GUEST_USER_INVITATIONS.EVERYONE.label,
            value: GUEST_USER_INVITATIONS.EVERYONE.value,
          },
        ],
      },
    ],
  },
  M365_TEMPORARY_ACCESS_PASS: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_TEMPORARY_ACCESS_PASS',
    checkName: 'temporaryaccesspass',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.TEMPORARYACCESSPASS.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.TEMPORARYACCESSPASS.CMMC_L2.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.TEMPORARYACCESSPASS.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.TEMPORARYACCESSPASS.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.TEMPORARYACCESSPASS.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.TEMPORARYACCESSPASS.HIPAA.DESCRIPTION',
        },
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'temporaryAccessPassState',
        title: 'Temporary Access Pass',
        statusOptions: [
          {
            label: 'Enabled',
            value: 'enabled',
            bestPractice: true,
          },
          {
            label: 'Disabled',
            value: 'disabled',
          },
        ],
        hubTaskType: TASK_TYPES.temporaryAccessPassConfig,
        hubAction: TASK_ACTIONS.update,
      },
    ],
  },
  M365_MFA_REGISTRATION_CAMPAIGN: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_MFA_REGISTRATION_CAMPAIGN',
    properties: [
      {
        name: 'authenticationMethodsRegistrationCampaignState',
        title: 'Microsoft MFA Campaign',
        statusOptions: [
          {
            label: 'Disabled',
            value: 'disabled',
          },
          {
            label: 'Enabled',
            value: 'enabled',
          },
          {
            label: 'Microsoft Managed',
            value: 'default',
          },
        ],
      },
    ],
  },
  M365_REJECT_DIRECT_SEND: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_REJECT_DIRECT_SEND',
    checkName: 'rejectDirectSend',
    portal: [PORTALS.EXCHANGE],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L2_6_5_5_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.REJECTDIRECTSEND.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.REJECTDIRECTSEND.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.REJECTDIRECTSEND.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.REJECTDIRECTSEND.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.REJECTDIRECTSEND.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.REJECTDIRECTSEND.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.REJECTDIRECTSEND.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.REJECTDIRECTSEND.CMMC_L2.DESCRIPTION',
        },
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'rejectDirectSend',
        title: 'Block Direct Send in Exchange Online',
        statusOptions: [
          {
            label: 'Blocked',
            value: true,
            bestPractice: true,
          },
          {
            label: 'Unblocked',
            value: false,
          },
        ],
        hubTaskType: TASK_TYPES.rejectDirectSend,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    baseline: {
      rejectDirectSend: true,
    },
  },
  M365_CALENDAR_SHARING: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_CALENDAR_SHARING',
    checkName: 'CISA.MS.EXO.6.2',
    portal: [PORTALS.M365_ADMIN_CENTER, PORTALS.EXCHANGE],
    checkModal: {
      configure: {
        alertContent: ({ securityCheck }) => getCalendarSharingCheckModalAlertContent({ securityCheck }),
      },
      complianceContent: { ...CIS_M365_V6_L2_1_3_3_COMPLIANCE_CONTENT, ...CISA_CISAMSEXO62_COMPLIANCE_CONTENT },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'calendarSharingEnabled',
        title: 'Default Sharing Policy',
        hubTaskType: TASK_TYPES.calendarSharing,
        hubAction: TASK_ACTIONS.update,
        checkModalIsDisabled: ({ securityCheck }) => isMissingExchangeOnlineCalendarSharingState(securityCheck),
        disabledTooltipId: ({ securityCheck }) =>
          getCalendarSharingMissingExchangeOnlineWarningMessageId(securityCheck),
        statusOptions: ({ currentSetting, isDisabled }) =>
          getCalendarSharingStatusOptions({ currentSetting, isDisabled }),
      },
    ],
    baseline: {
      calendarSharingEnabled: false,
    },
  },
  M365_IDLE_TIME_SESSION_TIMEOUT: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_IDLE_TIME_SESSION_TIMEOUT',
    checkName: 'activitybasedtimeoutpolicy',
    portal: [PORTALS.M365_ADMIN_CENTER],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L2_1_3_2_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ACTIVITYBASEDTIMEOUTPOLICY.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ACTIVITYBASEDTIMEOUTPOLICY.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ACTIVITYBASEDTIMEOUTPOLICY.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ACTIVITYBASEDTIMEOUTPOLICY.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ACTIVITYBASEDTIMEOUTPOLICY.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ACTIVITYBASEDTIMEOUTPOLICY.CMMC_L2.DESCRIPTION',
        },
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'isOrganizationDefaultActivityBasedPolicy',
        title: 'Activate organization default settings',
        statusOptions: [
          {
            label: 'Enabled',
            value: true,
            bestPractice: true,
          },
          {
            label: 'Disabled',
            value: false,
          },
        ],
        hubTaskType: TASK_TYPES.activitybasedtimeoutpolicy,
        hubAction: TASK_ACTIONS.update,
      },
      {
        name: 'usersWebSessionIdleTimeout',
        title: 'Idle session timeout',
        statusOptions: [
          {
            label: '01 Hour',
            value: '01:00:00',
          },
          {
            label: '03 Hours',
            value: '03:00:00',
          },
          {
            label: '06 Hours',
            value: '06:00:00',
          },
          {
            label: '08 Hours',
            value: '08:00:00',
          },
          {
            label: '12 Hours',
            value: '12:00:00',
          },
          {
            label: '24 Hours',
            value: '1.0:00:00',
          },
        ],
        hubTaskType: TASK_TYPES.activitybasedtimeoutpolicy,
        hubAction: TASK_ACTIONS.update,
      },
    ],
  },
  M365_ADMIN_MFA: {
    checkName: 'adminmfa',
    portal: [PORTALS.ENTRA],
    checkModal: {
      policyType: 'conditionalAccess',
      complianceContent: {
        ...essential8Content(
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.ESSENTIAL_8.TITLE',
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.ESSENTIAL_8.DESCRIPTION',
        ),
        ...CISA_ADMINMFA_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CIS_M365_V6_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.CIS_M365_V6_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.CIS_M365_V6_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.CIS_M365_V7_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.CIS_M365_V7_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ADMINMFA.CMMC_L2.DESCRIPTION',
        },
      },
      details: {
        entities: {
          labelId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.MFA_ADMIN_ACCOUNTS',
          noDataLabelId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.NONE_MATCHING_ACCOUNTS',
          includeAllResults: true,
          columns: [
            USER_ENTITY_COLUMNS.USER,
            USER_ENTITY_COLUMNS.ROLES,
            USER_ENTITY_COLUMNS.MFA_CONFIGURATION,
            USER_ENTITY_COLUMNS.MFA_STATUS,
          ],
        },
      },
    },
  },
  M365_USER_MFA: {
    checkName: 'usermfa',
    portal: [PORTALS.ENTRA],
    checkModal: {
      policyType: 'conditionalAccess',
      complianceContent: {
        ...CIS_M365_V6_L1_5_2_2_2_COMPLIANCE_CONTENT,
        ...essential8Content(
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USERMFA.ESSENTIAL_8.TITLE',
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USERMFA.ESSENTIAL_8.DESCRIPTION',
        ),
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USERMFA.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USERMFA.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USERMFA.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USERMFA.NIST_CSF_2_0.DESCRIPTION',
        },
        ...CISA_USERMFA_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USERMFA.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USERMFA.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USERMFA.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.USERMFA.CMMC_L2.DESCRIPTION',
        },
      },
      details: {
        entities: {
          labelId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.MFA_USER_ACCOUNTS',
          noDataLabelId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.NONE_MATCHING_ACCOUNTS',
          includeAllResults: true,
          columns: [USER_ENTITY_COLUMNS.USER, USER_ENTITY_COLUMNS.MFA_CONFIGURATION, USER_ENTITY_COLUMNS.MFA_STATUS],
        },
      },
    },
  },
  M365_SECURITY_DEFAULTS_ENABLED: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_SECURITY_DEFAULTS_ENABLED',
    checkName: 'securitydefaultsenabled',
    portal: [PORTALS.ENTRA],
    checkModal: {
      customConfigure: 'securityDefaults',
      configure: {
        alertMessageId: ({ securityCheck }) =>
          securityCheck?.compliance?.hasConditionalAccessPolicies ? SECURITY_DEFAULTS_CA_NOTICE_MESSAGE_ID : null,
      },
    },
    properties: [
      {
        name: 'isEnabled',
        title: 'isEnabled',
        displayTitleId: 'SECURE.SECURITY_DEFAULTS',
        statusOptions: ({ securityCheck }) => [
          {
            label: <FormattedMessage id="GENERAL.ENABLED" />,
            value: true,
            bestPractice: true,
            isDisabled: Boolean(securityCheck?.compliance?.hasConditionalAccessPolicies),
          },
          {
            label: <FormattedMessage id="GENERAL.DISABLED" />,
            value: false,
          },
        ],
        hubTaskType: TASK_TYPES.securityDefaults,
        hubAction: TASK_ACTIONS.update,
        checkModalIsDisabled: ({ currentSetting, securityCheck }) =>
          Boolean(securityCheck?.compliance?.hasConditionalAccessPolicies && currentSetting?.status === false),
      },
    ],
  },
  M365_SECURITY_DEFAULTS_DISABLED: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_SECURITY_DEFAULTS_DISABLED',
    checkName: 'securitydefaultsdisabled',
    portal: [PORTALS.ENTRA],
    checkModal: {
      customConfigure: 'securityDefaults',
      configure: {
        alertMessageId: ({ securityCheck }) => {
          if (securityCheck?.compliance?.hasConditionalAccessPolicies) {
            return SECURITY_DEFAULTS_CA_NOTICE_MESSAGE_ID
          }

          return securityCheck?.compliance?.hasConditionalAccessMfaPolicy
            ? null
            : SECURITY_DEFAULTS_NO_CA_MFA_NOTICE_MESSAGE_ID
        },
      },
    },
    properties: [
      {
        name: 'isEnabled',
        title: 'isEnabled',
        displayTitleId: 'SECURE.SECURITY_DEFAULTS',
        statusOptions: ({ securityCheck }) => [
          {
            label: <FormattedMessage id="GENERAL.ENABLED" />,
            value: true,
            isDisabled: Boolean(securityCheck?.compliance?.hasConditionalAccessPolicies),
          },
          {
            label: <FormattedMessage id="GENERAL.DISABLED" />,
            value: false,
            bestPractice: true,
          },
        ],
        hubTaskType: TASK_TYPES.securityDefaults,
        hubAction: TASK_ACTIONS.update,
        checkModalIsDisabled: ({ currentSetting, securityCheck }) =>
          Boolean(securityCheck?.compliance?.hasConditionalAccessPolicies && currentSetting?.status === false),
      },
    ],
  },
  M365_BLOCK_LEGACY_AUTHENTICATION: {
    checkName: 'blocklegacyauthentication',
    portal: [PORTALS.ENTRA],
    checkModal: {
      policyType: 'conditionalAccess',
      complianceContent: {
        ...CIS_M365_V6_L1_5_2_2_3_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKLEGACYAUTHENTICATION.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKLEGACYAUTHENTICATION.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKLEGACYAUTHENTICATION.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKLEGACYAUTHENTICATION.NIST_CSF_2_0.DESCRIPTION',
        },
        ...CISA_BLOCKLEGACYAUTHENTICATION_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKLEGACYAUTHENTICATION.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKLEGACYAUTHENTICATION.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKLEGACYAUTHENTICATION.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKLEGACYAUTHENTICATION.CMMC_L2.DESCRIPTION',
        },
      },
      details: {
        entities: {
          keyField: 'userId',
          labelId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.USER_ACCOUNTS',
          columns: [
            USER_ENTITY_COLUMNS.USER,
            {
              dataField: 'blockLegacyAuthenticationAccess',
              textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.LEGACY_AUTH',
              // blockLegacyAuthenticationAccess true means legacy auth IS blocked
              getValue: allowBlockStatus('blockLegacyAuthenticationAccess', false),
              auditSignal: true,
            },
          ],
        },
      },
    },
  },
  M365_INACTIVE_ACCOUNTS: {
    checkName: 'inactiveaccounts',
    portal: [PORTALS.ENTRA, PORTALS.M365_ADMIN_CENTER],
    checkModal: {
      customConfigure: 'inactiveAccounts',
      complianceContent: {
        ...essential8Content(
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INACTIVEACCOUNTS.ESSENTIAL_8.TITLE',
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INACTIVEACCOUNTS.ESSENTIAL_8.DESCRIPTION',
        ),
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INACTIVEACCOUNTS.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INACTIVEACCOUNTS.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INACTIVEACCOUNTS.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INACTIVEACCOUNTS.CMMC_L2.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INACTIVEACCOUNTS.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INACTIVEACCOUNTS.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INACTIVEACCOUNTS.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.INACTIVEACCOUNTS.HIPAA.DESCRIPTION',
        },
      },
      configure: {
        entities: {
          keyField: 'userId',
          labelId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.USER_ACCOUNTS',
          columns: [
            USER_ENTITY_COLUMNS.USER,
            {
              dataField: 'lastActive',
              textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.LAST_ACTIVE',
              sortable: true,
              getSortValue: getLastActiveSortValue,
              auditSignal: true,
            },
            {
              dataField: 'lastAppActivity',
              textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.LAST_APP_ACTIVITY',
              getValue: (row) =>
                row.lastAppActivity?.appName
                  ? `${row.lastAppActivity.appName} - ${row.lastAppActivity.activityDate}`
                  : null,
            },
          ],
        },
      },
    },
  },
  M365_RISKY_COUNTRIES: {
    checkName: 'riskycountries',
    portal: [PORTALS.ENTRA],
    checkModal: {
      policyType: 'conditionalAccess',
      complianceContent: {
        [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_15.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_15.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.RISKYCOUNTRIES.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.RISKYCOUNTRIES.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_RISKY_IPS: {
    checkName: 'riskyips',
    portal: [PORTALS.ENTRA],
    checkModal: {
      policyType: 'conditionalAccess',
      complianceContent: {
        [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_14.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_2_14.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.RISKYIPS.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.RISKYIPS.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_BLOCK_HIGH_RISK_USERS: {
    checkName: 'CISA.MS.AAD.2.1',
    portal: [PORTALS.ENTRA],
    checkModal: {
      policyType: 'conditionalAccess',
      complianceContent: { ...CIS_M365_V6_L1_5_2_2_7_COMPLIANCE_CONTENT, ...CISA_CISAMSAAD21_COMPLIANCE_CONTENT },
    },
  },
  M365_BLOCK_RISKY_SIGN_INS: {
    checkName: 'CISA.MS.AAD.2.3',
    portal: [PORTALS.ENTRA],
    checkModal: {
      policyType: 'conditionalAccess',
      complianceContent: { ...CIS_M365_V6_L1_5_2_2_6_COMPLIANCE_CONTENT, ...CISA_CISAMSAAD23_COMPLIANCE_CONTENT },
    },
  },
  M365_REREGISTER_MFA_MANAGED_DEVICES: {
    checkName: 'CISA.MS.AAD.3.8',
    portal: [PORTALS.ENTRA, PORTALS.INTUNE],
    checkModal: {
      policyType: 'conditionalAccess',
      complianceContent: { ...CIS_M365_V6_L1_5_2_2_10_COMPLIANCE_CONTENT, ...CISA_CISAMSAAD38_COMPLIANCE_CONTENT },
    },
  },
  M365_ADMIN_SIGNIN_FREQUENCY_FOR_ADMINS: {
    checkName: 'AUG.CIS.M365.5.2.2.4',
    portal: [PORTALS.ENTRA],
    checkModal: {
      policyType: 'conditionalAccess',
      complianceContent: CIS_M365_V6_L1_5_2_2_4_COMPLIANCE_CONTENT,
    },
  },
  M365_BLOCK_DEVICE_CODE_SIGN_IN_FLOW: {
    checkName: 'AUG.CIS.M365.5.2.2.12',
    portal: [PORTALS.ENTRA],
    checkModal: {
      policyType: 'conditionalAccess',
      complianceContent: CIS_M365_V6_L1_5_2_2_12_COMPLIANCE_CONTENT,
    },
  },
  M365_INTUNE_ENROLLMENT_SIGN_IN_FREQUENCY: {
    checkName: 'intuneenrollmentsigninfrequency',
    portal: [PORTALS.ENTRA],
    checkModal: {
      policyType: 'conditionalAccess',
      complianceContent: INTUNE_ENROLLMENT_SIGN_IN_FREQUENCY_COMPLIANCE_CONTENT,
    },
  },
  M365_IDLE_SESSION_TIMEOUT_UNMANAGED_DEVICES: {
    checkName: 'AUG.CIS.M365.1.3.2',
    portal: [PORTALS.ENTRA, PORTALS.M365_ADMIN_CENTER],
    checkModal: {
      policyType: 'conditionalAccess',
      complianceContent: {
        ...CIS_M365_V6_L2_1_3_2_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM365132.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM365132.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM365132.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM365132.HIPAA.DESCRIPTION',
        },
      },
    },
  },
  M365_REQUIRE_NEW_PASSWORD_HIGH_USER_RISK: {
    checkName: 'MT.1013',
    portal: [PORTALS.ENTRA],
    checkModal: {
      policyType: 'conditionalAccess',
      complianceContent: CIS_M365_V6_L1_5_2_2_6_COMPLIANCE_CONTENT,
    },
  },
  M365_BLOCK_UNKNOWN_UNSUPPORTED_DEVICE_PLATFORMS: {
    checkName: 'MT.1015',
    portal: [PORTALS.ENTRA],
    checkModal: {
      policyType: 'conditionalAccess',
    },
  },
  M365_NON_PERSISTENT_BROWSER_NON_CORPORATE_DEVICES: {
    checkName: 'MT.1017',
    portal: [PORTALS.ENTRA],
    checkModal: {
      policyType: 'conditionalAccess',
    },
  },
  M365_REQUIRE_COMPLIANT_OR_HYBRID_JOINED_ADMINS: {
    checkName: 'MT.1014',
    portal: [PORTALS.ENTRA, PORTALS.INTUNE],
    checkModal: {
      policyType: 'conditionalAccess',
    },
  },
  M365_REQUIRE_DEVICE_COMPLIANCE: {
    checkName: 'MT.1001',
    portal: [PORTALS.ENTRA, PORTALS.INTUNE],
    checkModal: {
      policyType: 'conditionalAccess',
      complianceContent: CIS_M365_V6_L1_5_2_2_9_COMPLIANCE_CONTENT,
    },
  },
  M365_DISABLE_SMTP_AUTH: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_DISABLE_SMTP_AUTH',
    checkName: 'CISA.MS.EXO.5.1',
    portal: [PORTALS.EXCHANGE],
    checkModal: {
      configure: {
        alertMessageId: DISABLE_SMTP_AUTH_WARNING_MESSAGE_ID,
        alertContent: ({ securityCheck }) => getDisableSmtpAuthCheckModalAlertContent({ securityCheck }),
      },
      complianceContent: { ...CIS_M365_V6_L1_6_5_4_COMPLIANCE_CONTENT, ...CISA_CISAMSEXO51_COMPLIANCE_CONTENT },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'smtpClientAuthenticationDisabled',
        displayTitle: 'SMTP AUTH',
        title: 'SMTP AUTH',
        statusOptions: getDisableSmtpAuthStatusOptions,
        hubTaskType: TASK_TYPES.disableSmtpAuth,
        hubAction: TASK_ACTIONS.update,
        checkModalIsDisabled: ({ securityCheck }) =>
          isMissingExchangeOnlineDisableSmtpAuthState(securityCheck) ||
          isMissingDisableSmtpAuthExchangeAdminRole(securityCheck),
        disabledTooltipId: ({ securityCheck }) => {
          if (isMissingExchangeOnlineDisableSmtpAuthState(securityCheck)) {
            return getDisableSmtpAuthMissingExchangeOnlineWarningMessageId(securityCheck)
          }

          if (isMissingDisableSmtpAuthExchangeAdminRole(securityCheck)) {
            return DISABLE_SMTP_AUTH_MISSING_EXCHANGE_ADMIN_WARNING_MESSAGE_ID
          }

          return null
        },
      },
    ],
    baseline: {
      smtpClientAuthenticationDisabled: true,
    },
  },
  M365_SECURITY_INFO_REGISTRATION_TRUSTED_LOCATION: {
    checkName: 'MT.1011',
    portal: [PORTALS.ENTRA],
    checkModal: {
      policyType: 'conditionalAccess',
    },
  },
  M365_MDO_ANTIPHISHINGPOLICIES: {
    checkName: 'mdo_antiphishingpolicies',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_7_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOANTIPHISHINGPOLICIES.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOANTIPHISHINGPOLICIES.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOANTIPHISHINGPOLICIES.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOANTIPHISHINGPOLICIES.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOANTIPHISHINGPOLICIES.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOANTIPHISHINGPOLICIES.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOANTIPHISHINGPOLICIES.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOANTIPHISHINGPOLICIES.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_PHISHTHRESHOLDLEVEL: {
    checkName: 'mdo_phishthresholdlevel',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_7_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOPHISHTHRESHOLDLEVEL.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOPHISHTHRESHOLDLEVEL.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOPHISHTHRESHOLDLEVEL.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOPHISHTHRESHOLDLEVEL.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_TARGETEDUSERSPROTECTION: {
    checkName: 'mdo_targetedusersprotection',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_7_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOTARGETEDUSERSPROTECTION.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOTARGETEDUSERSPROTECTION.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOTARGETEDUSERSPROTECTION.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOTARGETEDUSERSPROTECTION.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_ENABLEDOMAINSTOPROTECT: {
    checkName: 'mdo_enabledomainstoprotect',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_7_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOENABLEDOMAINSTOPROTECT.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOENABLEDOMAINSTOPROTECT.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOENABLEDOMAINSTOPROTECT.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOENABLEDOMAINSTOPROTECT.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_ENABLEMAILBOXINTELLIGENCE: {
    checkName: 'mdo_enablemailboxintelligence',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_7_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOENABLEMAILBOXINTELLIGENCE.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOENABLEMAILBOXINTELLIGENCE.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOENABLEMAILBOXINTELLIGENCE.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOENABLEMAILBOXINTELLIGENCE.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_MAILBOXINTELLIGENCEPROTECTION: {
    checkName: 'mdo_mailboxintelligenceprotection',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_7_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOMAILBOXINTELLIGENCEPROTECTION.HIPAA.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOMAILBOXINTELLIGENCEPROTECTION.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOMAILBOXINTELLIGENCEPROTECTION.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOMAILBOXINTELLIGENCEPROTECTION.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_TARGETEDUSERPROTECTIONACTION: {
    checkName: 'mdo_targeteduserprotectionaction',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_7_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOTARGETEDUSERPROTECTIONACTION.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOTARGETEDUSERPROTECTIONACTION.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOTARGETEDUSERPROTECTIONACTION.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOTARGETEDUSERPROTECTIONACTION.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_TARGETEDDOMAINPROTECTIONACTION: {
    checkName: 'mdo_targeteddomainprotectionaction',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_7_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOTARGETEDDOMAINPROTECTIONACTION.HIPAA.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOTARGETEDDOMAINPROTECTIONACTION.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOTARGETEDDOMAINPROTECTIONACTION.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOTARGETEDDOMAINPROTECTIONACTION.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_MAILBOXINTELLIGENCEPROTECTIONACTION: {
    checkName: 'mdo_mailboxintelligenceprotectionaction',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_7_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOMAILBOXINTELLIGENCEPROTECTIONACTION.HIPAA.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOMAILBOXINTELLIGENCEPROTECTIONACTION.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOMAILBOXINTELLIGENCEPROTECTIONACTION.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOMAILBOXINTELLIGENCEPROTECTIONACTION.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_SIMILARUSERSSAFETYTIPS: {
    checkName: 'mdo_similaruserssafetytips',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_7_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSIMILARUSERSSAFETYTIPS.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSIMILARUSERSSAFETYTIPS.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSIMILARUSERSSAFETYTIPS.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSIMILARUSERSSAFETYTIPS.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_SIMILARDOMAINSSAFETYTIPS: {
    checkName: 'mdo_similardomainssafetytips',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_7_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSIMILARDOMAINSSAFETYTIPS.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSIMILARDOMAINSSAFETYTIPS.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSIMILARDOMAINSSAFETYTIPS.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSIMILARDOMAINSSAFETYTIPS.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_UNUSUALCHARACTERSSAFETYTIPS: {
    checkName: 'mdo_unusualcharacterssafetytips',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_7_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOUNUSUALCHARACTERSSAFETYTIPS.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOUNUSUALCHARACTERSSAFETYTIPS.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOUNUSUALCHARACTERSSAFETYTIPS.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOUNUSUALCHARACTERSSAFETYTIPS.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_SPAM_NOTIFICATIONS_ONLY_FOR_ADMINS: {
    checkName: 'mdo_spam_notifications_only_for_admins',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: CIS_M365_V6_L1_2_1_6_COMPLIANCE_CONTENT,
    },
  },
  M365_MDO_THRESHOLDREACHEDACTION: {
    checkName: 'mdo_thresholdreachedaction',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L1_2_1_15_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOTHRESHOLDREACHEDACTION.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOTHRESHOLDREACHEDACTION.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_RECIPIENTEXTERNALLIMITPERHOUR: {
    checkName: 'mdo_recipientexternallimitperhour',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L1_2_1_15_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDORECIPIENTEXTERNALLIMITPERHOUR.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDORECIPIENTEXTERNALLIMITPERHOUR.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_RECIPIENTINTERNALLIMITPERHOUR: {
    checkName: 'mdo_recipientinternallimitperhour',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L1_2_1_15_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDORECIPIENTINTERNALLIMITPERHOUR.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDORECIPIENTINTERNALLIMITPERHOUR.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_RECIPIENTLIMITPERDAY: {
    checkName: 'mdo_recipientlimitperday',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L1_2_1_15_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDORECIPIENTLIMITPERDAY.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDORECIPIENTLIMITPERDAY.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_SAFELINKSFOREMAIL: {
    checkName: 'mdo_safelinksforemail',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_1_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFELINKSFOREMAIL.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFELINKSFOREMAIL.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFELINKSFOREMAIL.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFELINKSFOREMAIL.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFELINKSFOREMAIL.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFELINKSFOREMAIL.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFELINKSFOREMAIL.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFELINKSFOREMAIL.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_SAFELINKSFOR_OFFICE_APPS: {
    checkName: 'mdo_safelinksforOfficeApps',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_1_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFELINKSFOROFFICEAPPS.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFELINKSFOROFFICEAPPS.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFELINKSFOROFFICEAPPS.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFELINKSFOROFFICEAPPS.HIPAA.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_SAFEATTACHMENTS: {
    checkName: 'mdo_safeattachments',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_4_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEATTACHMENTS.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEATTACHMENTS.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEATTACHMENTS.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEATTACHMENTS.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_SAFEATTACHMENTPOLICY: {
    checkName: 'mdo_safeattachmentpolicy',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_4_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEATTACHMENTPOLICY.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEATTACHMENTPOLICY.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEATTACHMENTPOLICY.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEATTACHMENTPOLICY.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_ATPPROTECTION: {
    checkName: 'mdo_atpprotection',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_5_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOATPPROTECTION.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOATPPROTECTION.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOATPPROTECTION.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOATPPROTECTION.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOATPPROTECTION.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOATPPROTECTION.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOATPPROTECTION.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOATPPROTECTION.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_SAFEDOCUMENTS: {
    checkName: 'mdo_safedocuments',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_5_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEDOCUMENTS.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEDOCUMENTS.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEDOCUMENTS.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEDOCUMENTS.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEDOCUMENTS.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEDOCUMENTS.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEDOCUMENTS.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSAFEDOCUMENTS.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_ZAPMALWARE: {
    checkName: 'mdo_zapmalware',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOZAPMALWARE.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOZAPMALWARE.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOZAPMALWARE.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOZAPMALWARE.CMMC_L2.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOZAPMALWARE.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOZAPMALWARE.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOZAPMALWARE.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOZAPMALWARE.HIPAA.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_COMMONATTACHMENTSFILTER: {
    checkName: 'mdo_commonattachmentsfilter',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L1_2_1_2_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOCOMMONATTACHMENTSFILTER.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOCOMMONATTACHMENTSFILTER.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOCOMMONATTACHMENTSFILTER.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOCOMMONATTACHMENTSFILTER.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOCOMMONATTACHMENTSFILTER.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOCOMMONATTACHMENTSFILTER.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_BLOCKMAILFORWARD: {
    checkName: 'mdo_blockmailforward',
    portal: [PORTALS.DEFENDER, PORTALS.EXCHANGE],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_6_2_1_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOBLOCKMAILFORWARD.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOBLOCKMAILFORWARD.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_AUTOFORWARDINGMODE: {
    checkName: 'mdo_autoforwardingmode',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L1_6_2_1_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOAUTOFORWARDINGMODE.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOAUTOFORWARDINGMODE.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_MS_EXO_2_2: {
    checkName: 'CISA.MS.EXO.2.2',
    portal: [PORTALS.DNS_EXTERNAL, PORTALS.EXCHANGE],
    checkModal: {
      complianceContent: { ...CIS_M365_V6_L1_2_1_8_COMPLIANCE_CONTENT, ...CISA_CISAMSEXO22_COMPLIANCE_CONTENT },
    },
  },
  M365_MS_EXO_7_1: {
    checkName: 'CISA.MS.EXO.7.1',
    portal: [PORTALS.EXCHANGE],
    checkModal: {
      complianceContent: { ...CIS_M365_V6_L1_6_2_3_COMPLIANCE_CONTENT, ...CISA_CISAMSEXO71_COMPLIANCE_CONTENT },
    },
  },
  M365_MS_AAD_3_3: {
    checkName: 'CISA.MS.AAD.3.3',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: { ...CIS_M365_V6_L1_5_2_3_1_COMPLIANCE_CONTENT, ...CISA_CISAMSAAD33_COMPLIANCE_CONTENT },
    },
  },
  M365_SELF_SERVE_PASSWORD_RESET: {
    checkName: 'selfservepasswordreset',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_5_2_4_1_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SELFSERVEPASSWORDRESET.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SELFSERVEPASSWORDRESET.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SELFSERVEPASSWORDRESET.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SELFSERVEPASSWORDRESET.NIST_CSF_2_0.DESCRIPTION',
        },
      },
      details: {
        entities: {
          keyField: 'userId',
          labelId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.USER_ACCOUNTS',
          columns: [
            USER_ENTITY_COLUMNS.USER,
            {
              // backend sets this to the display-ready string 'Enabled' / 'Disabled'
              dataField: 'selfServicePasswordReset',
              textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.SSPR_STATUS',
              auditSignal: true,
            },
          ],
        },
      },
    },
  },
  M365_MS_EXO_4_1: {
    checkName: 'CISA.MS.EXO.4.1',
    portal: [PORTALS.DNS_EXTERNAL, PORTALS.EXCHANGE],
    checkModal: {
      complianceContent: { ...CIS_M365_V6_L1_2_1_10_COMPLIANCE_CONTENT, ...CISA_CISAMSEXO41_COMPLIANCE_CONTENT },
    },
  },
  M365_MS_EXO_4_2: {
    checkName: 'CISA.MS.EXO.4.2',
    portal: [PORTALS.DNS_EXTERNAL, PORTALS.EXCHANGE],
    checkModal: {
      complianceContent: { ...CISA_CISAMSEXO42_COMPLIANCE_CONTENT },
    },
  },
  M365_MS_EXO_4_3: {
    checkName: 'CISA.MS.EXO.4.3',
    portal: [PORTALS.DNS_EXTERNAL, PORTALS.EXCHANGE],
    checkModal: {
      complianceContent: { ...CISA_CISAMSEXO43_COMPLIANCE_CONTENT },
    },
  },
  M365_MS_AAD_7_1: {
    checkName: 'CISA.MS.AAD.7.1',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_1_1_3_COMPLIANCE_CONTENT,
        ...CISA_CISAMSAAD71_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD71.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD71.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_MS_AAD_7_3: {
    checkName: 'CISA.MS.AAD.7.3',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_1_1_1_COMPLIANCE_CONTENT,
        ...CISA_CISAMSAAD73_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD73.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD73.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_MT_1026: {
    checkName: 'MT.1026',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_1_1_1_COMPLIANCE_CONTENT,
        ...essential8Content(
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MT1026.ESSENTIAL_8.TITLE',
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MT1026.ESSENTIAL_8.DESCRIPTION',
        ),
      },
    },
  },
  M365_MT_1030: {
    checkName: 'MT.1030',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: essential8Content(
        'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MT1030.ESSENTIAL_8.TITLE',
        'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MT1030.ESSENTIAL_8.DESCRIPTION',
      ),
    },
  },
  M365_MS_AAD_3_6: {
    checkName: 'CISA.MS.AAD.3.6',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L2_5_2_2_5_COMPLIANCE_CONTENT,
        ...essential8Content(
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD36.ESSENTIAL_8.TITLE',
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD36.ESSENTIAL_8.DESCRIPTION',
        ),
        ...CISA_CISAMSAAD36_COMPLIANCE_CONTENT,
      },
    },
  },
  M365_MS_AAD_3_1: {
    checkName: 'CISA.MS.AAD.3.1',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...essential8Content(
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD31.ESSENTIAL_8.TITLE',
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD31.ESSENTIAL_8.DESCRIPTION',
        ),
        ...CISA_CISAMSAAD31_COMPLIANCE_CONTENT,
      },
    },
  },
  M365_MS_AAD_3_7: {
    checkName: 'CISA.MS.AAD.3.7',
    portal: [PORTALS.ENTRA, PORTALS.INTUNE],
    checkModal: {
      complianceContent: { ...CIS_M365_V6_L1_5_2_2_10_COMPLIANCE_CONTENT, ...CISA_CISAMSAAD37_COMPLIANCE_CONTENT },
    },
  },
  M365_SHAREPOINT_EXTERNAL_GUEST_SHARING: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_SHAREPOINT_EXTERNAL_GUEST_SHARING',
    checkName: 'sharepointexternalguestsharing',
    portal: [PORTALS.SHAREPOINT],
    hubTaskType: TASK_TYPES.sharePointTenantSettings,
    hubAction: TASK_ACTIONS.update,
    postureTemplate: true,
    properties: [
      {
        name: 'sharingCapability',
        title: 'SharePoint content can be shared with:',
        statusOptions: [
          { label: 'Anyone', value: 'ExternalUserAndGuestSharing' },
          { label: 'New and existing guests', value: 'ExternalUserSharingOnly', bestPractice: true },
          { label: 'Existing guests', value: 'ExistingExternalUserSharingOnly', compliant: true },
          { label: 'Only people in your organization', value: 'Disabled', compliant: true },
        ],
        hubTaskType: TASK_TYPES.sharePointTenantSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_7_2_3_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALGUESTSHARING.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALGUESTSHARING.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALGUESTSHARING.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALGUESTSHARING.NIST_CSF_2_0.DESCRIPTION',
        },
        ...CISA_SHAREPOINTEXTERNALGUESTSHARING_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALGUESTSHARING.CMMC_L1.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALGUESTSHARING.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALGUESTSHARING.CMMC_L2.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTEXTERNALGUESTSHARING.CMMC_L2.DESCRIPTION',
        },
      },
      customConfigure: 'spoSharingSettings',
    },
  },
  M365_SHAREPOINT_DOMAIN_ALLOWLIST: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_SHAREPOINT_DOMAIN_ALLOWLIST',
    checkName: 'sharepointdomainallowlist',
    portal: [PORTALS.SHAREPOINT],
    hubTaskType: TASK_TYPES.sharepointSettings,
    hubAction: TASK_ACTIONS.update,
    // required by PostureConfigureTemplate to resolve tasktype/action for hub tasks
    properties: [
      {
        hubTaskType: TASK_TYPES.sharepointSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    checkModal: {
      complianceContent: CIS_M365_V6_L2_7_2_6_COMPLIANCE_CONTENT,
      customConfigure: 'sharepointDomainAllowlist',
    },
  },
  M365_SHAREPOINT_MODERN_AUTH_REQUIRED: {
    checkName: 'sharepointmodernauthrequired',
    portal: [PORTALS.SHAREPOINT],
    postureTemplate: true,
    properties: [
      {
        name: 'isLegacyAuthProtocolsEnabled',
        // the title needs to match what we receive from the GET request in compliance->settings
        title: "Apps that don't use modern authentication",
        statusOptions: [
          {
            label: 'Block access',
            value: false,
            bestPractice: true,
          },
          {
            label: 'Allow access',
            value: true,
          },
        ],
        hubTaskType: TASK_TYPES.sharepointSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_7_2_1_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTMODERNAUTHREQUIRED.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTMODERNAUTHREQUIRED.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTMODERNAUTHREQUIRED.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTMODERNAUTHREQUIRED.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTMODERNAUTHREQUIRED.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTMODERNAUTHREQUIRED.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_ONEDRIVE_CONTENT_SHARING_RESTRICTED: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_ONEDRIVE_CONTENT_SHARING_RESTRICTED',
    checkName: 'onedrivecontentsharingrestricted',
    portal: [PORTALS.SHAREPOINT],
    hubTaskType: TASK_TYPES.sharePointTenantSettings,
    hubAction: TASK_ACTIONS.update,
    properties: [
      {
        name: 'odbSharingCapability',
        title: 'OneDrive content can be shared with:',
        statusOptions: [
          { label: 'Anyone', value: 'ExternalUserAndGuestSharing' },
          { label: 'New and existing guests', value: 'ExternalUserSharingOnly' },
          { label: 'Existing guests', value: 'ExistingExternalUserSharingOnly' },
          { label: 'Only people in your organization', value: 'Disabled', bestPractice: true },
        ],
        hubTaskType: TASK_TYPES.sharePointTenantSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L2_7_2_4_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ONEDRIVECONTENTSHARINGRESTRICTED.CMMC_L1.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ONEDRIVECONTENTSHARINGRESTRICTED.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ONEDRIVECONTENTSHARINGRESTRICTED.CMMC_L2.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.ONEDRIVECONTENTSHARINGRESTRICTED.CMMC_L2.DESCRIPTION',
        },
      },
      customConfigure: 'spoSharingSettings',
    },
  },
  M365_SHAREPOINT_DEFAULT_SHARING_LINK_TYPE: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_SHAREPOINT_DEFAULT_SHARING_LINK_TYPE',
    checkName: 'sharepointdefaultsharinglinktype',
    portal: [PORTALS.SHAREPOINT],
    hubTaskType: TASK_TYPES.sharePointTenantSettings,
    hubAction: TASK_ACTIONS.update,
    postureTemplate: true,
    properties: [
      {
        name: 'defaultSharingLinkType',
        title: 'Default sharing link type',
        statusOptions: [
          { label: 'None (use organization default)', value: 0 },
          { label: 'Specific people (only the people the user specifies)', value: 1, bestPractice: true },
          { label: 'Only people in your organization', value: 2 },
          { label: 'Anyone with the link', value: 3 },
          { label: 'People in your organization and guests with existing access', value: 4 },
        ],
        hubTaskType: TASK_TYPES.sharePointTenantSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_7_2_7_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDEFAULTSHARINGLINKTYPE.CMMC_L1.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDEFAULTSHARINGLINKTYPE.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDEFAULTSHARINGLINKTYPE.CMMC_L2.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SHAREPOINTDEFAULTSHARINGLINKTYPE.CMMC_L2.DESCRIPTION',
        },
      },
      customConfigure: 'spoSharingSettings',
    },
  },
  M365_SHAREPOINT_AZURE_AD_B2B_INTEGRATION: {
    checkName: 'sharepointazureadb2bintegration',
    portal: [PORTALS.SHAREPOINT],
    postureTemplate: true,
    properties: [
      {
        name: 'enableAzureADB2BIntegration',
        title: 'SharePoint Azure AD B2B Integration',
        statusOptions: [
          {
            label: 'Enabled',
            value: true,
            bestPractice: true,
          },
          {
            label: 'Disabled',
            value: false,
          },
        ],
        hubTaskType: TASK_TYPES.sharePointTenantSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    checkModal: { complianceContent: CIS_M365_V6_L1_7_2_2_COMPLIANCE_CONTENT },
  },
  M365_SHAREPOINT_DISALLOW_INFECTED_FILE_DOWNLOAD: {
    checkName: 'sharepointdisallowinfectedfiledownload',
    portal: [PORTALS.SHAREPOINT],
    postureTemplate: true,
    properties: [
      {
        name: 'disallowInfectedFileDownload',
        title: 'SharePoint Download Infected Files',
        statusOptions: [
          {
            label: 'Blocked',
            value: true,
            bestPractice: true,
          },
          {
            label: 'Allowed',
            value: false,
          },
        ],
        hubTaskType: TASK_TYPES.sharePointTenantSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    checkModal: { complianceContent: CIS_M365_V6_L2_7_3_1_COMPLIANCE_CONTENT },
  },
  M365_SHAREPOINT_SYNC_CLIENT_RESTRICTION: {
    checkName: 'sharepointsyncclientrestriction',
    portal: [PORTALS.SHAREPOINT],
    checkModal: { complianceContent: CIS_M365_V6_L2_7_3_2_COMPLIANCE_CONTENT },
  },
  M365_SHAREPOINT_EXTERNAL_SHARING_SECURITY_GROUP: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_SHAREPOINT_EXTERNAL_SHARING_SECURITY_GROUP',
    checkName: 'sharepointexternalsharingsecuritygroup',
    portal: [PORTALS.SHAREPOINT],
    hubTaskType: TASK_TYPES.sharePointTenantSettings,
    hubAction: TASK_ACTIONS.update,
    properties: [
      {
        hubTaskType: TASK_TYPES.sharePointTenantSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    checkModal: {
      complianceContent: CIS_M365_V6_L2_7_2_8_COMPLIANCE_CONTENT,
      customConfigure: 'sharepointExternalSharingSecurityGroup',
    },
  },
  M365_SHAREPOINT_GUEST_EXPIRATION: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_SHAREPOINT_GUEST_EXPIRATION',
    checkName: 'sharepointguestexpiration',
    portal: [PORTALS.SHAREPOINT],
    hubTaskType: TASK_TYPES.sharePointTenantSettings,
    hubAction: TASK_ACTIONS.update,
    postureTemplate: true,
    properties: [
      {
        name: 'externalUserExpirationRequired',
        title: 'Automatic Guest Access Expiration',
        statusOptions: [
          { label: 'Enabled', value: true, bestPractice: true },
          { label: 'Disabled', value: false },
        ],
        hubTaskType: TASK_TYPES.sharePointTenantSettings,
        hubAction: TASK_ACTIONS.update,
      },
      {
        name: 'externalUserExpireInDays',
        title: 'Guest Access Expire In Days',
        statusOptions: [{ label: '30 days', value: 30, bestPractice: true }],
        hubTaskType: TASK_TYPES.sharePointTenantSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    checkModal: {
      complianceContent: CIS_M365_V6_L1_7_2_9_COMPLIANCE_CONTENT,
      customConfigure: 'spoSharingSettings',
    },
  },
  M365_SHAREPOINT_GUEST_REAUTHENTICATION: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_SHAREPOINT_GUEST_REAUTHENTICATION',
    checkName: 'sharepointguestreauthentication',
    portal: [PORTALS.SHAREPOINT],
    hubTaskType: TASK_TYPES.sharePointTenantSettings,
    hubAction: TASK_ACTIONS.update,
    postureTemplate: true,
    properties: [
      {
        name: 'emailAttestationRequired',
        title: 'Verification Code Reauthentication Required',
        statusOptions: [
          { label: 'Enabled', value: true, bestPractice: true },
          { label: 'Disabled', value: false },
        ],
        hubTaskType: TASK_TYPES.sharePointTenantSettings,
        hubAction: TASK_ACTIONS.update,
      },
      {
        name: 'emailAttestationReAuthDays',
        title: 'Reauthentication Days',
        statusOptions: [{ label: '15 days', value: 15, bestPractice: true }],
        hubTaskType: TASK_TYPES.sharePointTenantSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    checkModal: {
      complianceContent: CIS_M365_V6_L1_7_2_10_COMPLIANCE_CONTENT,
      customConfigure: 'spoSharingSettings',
    },
  },
  M365_SHAREPOINT_DEFAULT_LINK_PERMISSION: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_SHAREPOINT_DEFAULT_LINK_PERMISSION',
    checkName: 'sharepointdefaultlinkpermission',
    portal: [PORTALS.SHAREPOINT],
    hubTaskType: TASK_TYPES.sharePointTenantSettings,
    hubAction: TASK_ACTIONS.update,
    postureTemplate: true,
    properties: [
      {
        name: 'defaultLinkPermission',
        title: 'SharePoint Default Link Permission',
        statusOptions: [
          { label: 'View', value: 1, bestPractice: true },
          { label: 'Edit', value: 2 },
        ],
        hubTaskType: TASK_TYPES.sharePointTenantSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    checkModal: {
      complianceContent: CIS_M365_V6_L1_7_2_11_COMPLIANCE_CONTENT,
      customConfigure: 'spoSharingSettings',
    },
  },
  M365_MS_AAD_8_2: {
    checkName: 'CISA.MS.AAD.8.2',
    portal: [PORTALS.ENTRA],
    postureTemplate: true,
    checkModal: {
      complianceContent: { ...CIS_M365_V6_L2_5_1_6_3_COMPLIANCE_CONTENT, ...CISA_CISAMSAAD82_COMPLIANCE_CONTENT },
      configure: {
        alertContent: ({ securityCheck }) => {
          const status = securityCheck?.compliance?.settings?.[0]?.status
          if (status === GUEST_USER_INVITATIONS.EVERYONE.label) {
            return <FormattedMessage id="SECURE.GUEST_USER_INVITATIONS_RESTRICTED.EVERYONE_WARNING" />
          }
          if (status === GUEST_USER_INVITATIONS.ADMINS_GUEST_INVITERS_AND_ALL_MEMBERS.label) {
            return <FormattedMessage id="SECURE.GUEST_USER_INVITATIONS_RESTRICTED.ADMINS_AND_MEMBERS_WARNING" />
          }
          if (status === GUEST_USER_INVITATIONS.ADMINS_AND_GUEST_INVITERS.label) {
            return <FormattedMessage id="SECURE.GUEST_USER_INVITATIONS_RESTRICTED.ADMINS_AND_INVITERS_NOTE" />
          }
          return null
        },
      },
    },
    properties: [
      {
        name: 'allowInvitesFrom',
        title: 'Guest user invite settings',
        statusOptions: [
          {
            label: GUEST_USER_INVITATIONS.NONE.label,
            value: GUEST_USER_INVITATIONS.NONE.value,
          },
          {
            label: GUEST_USER_INVITATIONS.ADMINS_AND_GUEST_INVITERS.label,
            value: GUEST_USER_INVITATIONS.ADMINS_AND_GUEST_INVITERS.value,
            bestPractice: true,
          },
          {
            label: GUEST_USER_INVITATIONS.ADMINS_GUEST_INVITERS_AND_ALL_MEMBERS.label,
            value: GUEST_USER_INVITATIONS.ADMINS_GUEST_INVITERS_AND_ALL_MEMBERS.value,
          },
          {
            label: GUEST_USER_INVITATIONS.EVERYONE.label,
            value: GUEST_USER_INVITATIONS.EVERYONE.value,
          },
        ],
        hubTaskType: TASK_TYPES.guestUserInvitationsRestricted,
        hubAction: TASK_ACTIONS.update,
      },
    ],
  },
  M365_MS_AAD_8_1: {
    checkName: 'CISA.MS.AAD.8.1',
    portal: [PORTALS.ENTRA],
    postureTemplate: true,
    checkModal: {
      complianceContent: { ...CIS_M365_V6_L1_5_1_6_2_COMPLIANCE_CONTENT, ...CISA_CISAMSAAD81_COMPLIANCE_CONTENT },
      configure: {
        alertContent: ({ securityCheck }) => {
          const status = securityCheck?.compliance?.settings?.[0]?.status
          if (status === GUEST_USER_ACCESS.SAME_AS_MEMBER.label) {
            return <FormattedMessage id="SECURE.GUEST_USER_ACCESS_RESTRICTED.SAME_AS_MEMBER_WARNING" />
          }
          if (status === GUEST_USER_ACCESS.MOST_RESTRICTIVE.label) {
            return <FormattedMessage id="SECURE.GUEST_USER_ACCESS_RESTRICTED.VIVA_ENGAGE_NOTE" />
          }
          return null
        },
      },
    },
    properties: [
      {
        name: 'guestUserRoleId',
        title: 'Guest user access',
        statusOptions: [
          {
            label: GUEST_USER_ACCESS.SAME_AS_MEMBER.label,
            value: GUEST_USER_ACCESS.SAME_AS_MEMBER.roleId,
          },
          {
            label: GUEST_USER_ACCESS.LIMITED.label,
            value: GUEST_USER_ACCESS.LIMITED.roleId,
            bestPractice: true,
          },
          {
            label: GUEST_USER_ACCESS.MOST_RESTRICTIVE.label,
            value: GUEST_USER_ACCESS.MOST_RESTRICTIVE.roleId,
          },
        ],
        hubTaskType: TASK_TYPES.guestUserAccessRestricted,
        hubAction: TASK_ACTIONS.update,
      },
    ],
  },
  M365_EIDSCA_AP08: {
    checkName: 'EIDSCA.AP08',
    portal: [PORTALS.ENTRA],
    checkModal: { complianceContent: CISA_EIDSCAAP08_COMPLIANCE_CONTENT },
  },
  M365_EIDSCA_AP07: {
    checkName: 'EIDSCA.AP07',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: { ...CIS_M365_V6_L1_5_1_6_2_COMPLIANCE_CONTENT, ...CISA_EIDSCAAP07_COMPLIANCE_CONTENT },
    },
  },
  M365_EIDSCA_AP10: {
    checkName: 'EIDSCA.AP10',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: { ...CIS_M365_V6_L2_5_1_2_2_COMPLIANCE_CONTENT, ...CISA_EIDSCAAP10_COMPLIANCE_CONTENT },
    },
  },
  M365_EIDSCA_AP14: {
    checkName: 'EIDSCA.AP14',
    portal: [PORTALS.ENTRA],
  },
  M365_EIDSCA_PR03: {
    checkName: 'EIDSCA.PR03',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_5_2_3_2_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAPR03.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAPR03.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_EIDSCA_PR01: {
    checkName: 'EIDSCA.PR01',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_5_2_3_3_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAPR01.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAPR01.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_EIDSCA_PR02: {
    checkName: 'EIDSCA.PR02',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAPR02.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAPR02.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_EIDSCA_PR06: {
    checkName: 'EIDSCA.PR06',
    portal: [PORTALS.ENTRA],
    ...SMART_LOCKOUT_CONFIGURE,
    postureTemplate: true,
    properties: [SMART_LOCKOUT_THRESHOLD_PROPERTY],
    baseline: {
      lockoutThreshold: 10,
    },
    checkModal: {
      ...SMART_LOCKOUT_CONFIGURE.checkModal,
      complianceContent: {
        [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_3_8.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_3_8.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAPR06.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAPR06.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_EIDSCA_PR05: {
    checkName: 'EIDSCA.PR05',
    portal: [PORTALS.ENTRA],
    ...SMART_LOCKOUT_CONFIGURE,
    postureTemplate: true,
    properties: [SMART_LOCKOUT_DURATION_PROPERTY],
    baseline: {
      lockoutDurationInSeconds: 60,
    },
    checkModal: {
      ...SMART_LOCKOUT_CONFIGURE.checkModal,
      complianceContent: {
        [COMPLIANCE_TYPES.CIS_M365_V7_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_3_9.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_M365_V7_L1.5_2_3_9.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAPR05.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EIDSCAPR05.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_MS_AAD_3_5: {
    checkName: 'CISA.MS.AAD.3.5',
    portal: [PORTALS.ENTRA],
    ...AUTHENTICATION_METHODS_POLICY_CONFIGURE,
    checkModal: {
      ...AUTHENTICATION_METHODS_POLICY_CONFIGURE.checkModal,
      complianceContent: { ...CIS_M365_V6_L1_5_2_3_5_COMPLIANCE_CONTENT, ...CISA_CISAMSAAD35_COMPLIANCE_CONTENT },
    },
  },
  M365_CIS_M365_5_2_3_5: {
    checkName: 'CIS.M365.5.2.3.5',
    portal: [PORTALS.ENTRA],
    ...AUTHENTICATION_METHODS_POLICY_CONFIGURE,
    checkModal: {
      ...AUTHENTICATION_METHODS_POLICY_CONFIGURE.checkModal,
      complianceContent: {
        ...AUTHENTICATION_METHODS_POLICY_CONFIGURE.checkModal.complianceContent,
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISM3655235.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISM3655235.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISM3655235.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISM3655235.HIPAA.DESCRIPTION',
        },
      },
    },
  },
  M365_AUG_CIS_M365_5_2_3_6: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_SYSTEM_PREFERRED_MFA',
    checkName: 'AUG.CIS.M365.5.2.3.6',
    portal: [PORTALS.ENTRA],
    checkModal: { complianceContent: CIS_M365_V6_L1_5_2_3_6_COMPLIANCE_CONTENT },
    postureTemplate: true,
    properties: [
      {
        name: 'systemCredentialPreferences',
        title: 'System-preferred multifactor authentication',
        statusOptions: [
          {
            label: 'Enabled',
            value: buildSystemPreferredMfaTarget(),
            bestPractice: true,
          },
          {
            label: 'Microsoft Managed',
            value: buildSystemPreferredMfaTarget('default'),
          },
          {
            label: 'Disabled',
            value: buildSystemPreferredMfaTarget('disabled'),
          },
        ],
        hubTaskType: TASK_TYPES.authenticationMethodsPolicy,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    baseline: {
      systemCredentialPreferences: buildSystemPreferredMfaTarget(),
    },
  },
  M365_AUG_CIS_M365_5_1_2_1: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_PER_USER_MFA_DISABLED',
    checkName: 'AUG.CIS.M365.5.1.2.1',
    portal: [PORTALS.ENTRA],
    postureTemplate: true,
    checkModal: {
      complianceContent: CIS_M365_V6_L1_5_1_2_1_COMPLIANCE_CONTENT,
      customConfigure: 'perUserMfaDisabled',
    },
    properties: [
      {
        name: 'perUserMfa',
        title: 'Per-user MFA state',
        statusOptions: [
          {
            label: 'Disabled',
            value: 'disabled',
            bestPractice: true,
          },
        ],
        hubTaskType: TASK_TYPES.perUserMfa,
        hubAction: TASK_ACTIONS.update,
      },
    ],
  },
  M365_AUG_CIS_M365_5_1_3_1: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_AUG_CIS_M365_5_1_3_1',
    checkName: 'AUG.CIS.M365.5.1.3.1',
    portal: [PORTALS.ENTRA],
    postureTemplate: true,
    checkModal: {
      complianceContent: CIS_M365_V6_L1_5_1_3_1_COMPLIANCE_CONTENT,
      customConfigure: 'groupTemplate',
      groupTemplateName: DYNAMIC_GUEST_GROUP_TEMPLATE_NAME,
      groupTemplateTag: DYNAMIC_GUEST_GROUP_TEMPLATE_TAG,
    },
  },
  M365_AUG_CIS_M365_5_1_3_2: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_AUG_CIS_M365_5_1_3_2',
    checkName: 'AUG.CIS.M365.5.1.3.2',
    portal: [PORTALS.ENTRA],
    checkModal: { complianceContent: CIS_M365_V6_L1_5_1_3_2_COMPLIANCE_CONTENT },
    postureTemplate: true,
    properties: [
      {
        name: 'allowedToCreateSecurityGroups',
        title: 'Users can create security groups in Azure portals, API or PowerShell',
        statusOptions: getSecurityGroupCreationStatusOptions,
        hubTaskType: TASK_TYPES.securityGroupCreationPolicy,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    baseline: {
      allowedToCreateSecurityGroups: false,
    },
  },
  M365_AUG_CIS_M365_6_1_2: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_AUG_CIS_M365_6_1_2',
    checkName: 'AUG.CIS.M365.6.1.2',
    portal: [PORTALS.EXCHANGE],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_6_1_2_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CIS_V8_1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.8_2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CIS_V8.8_2.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM365612.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM365612.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM365612.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.AUGCISM365612.HIPAA.DESCRIPTION',
        },
      },
      customConfigure: 'mailboxAuditActions',
      details: {
        entities: {
          keyField: 'title',
          labelId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.MAILBOX_AUDIT_ACTIONS_MAILBOXES',
          columns: [
            {
              dataField: 'title',
              textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.MAILBOX',
              headerTooltipId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.MAILBOX_TOOLTIP',
              sortable: true,
              searchable: true,
            },
            {
              dataField: 'auditEnabled',
              textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.AUDIT_ENABLED',
              headerTooltipId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.AUDIT_ENABLED_TOOLTIP',
              getValue: (row, intl) =>
                intl.formatMessage({
                  id: row.auditEnabled
                    ? 'COMPLIANCE_AUDIT.CHECK.DETAILS.ENABLED'
                    : 'COMPLIANCE_AUDIT.CHECK.DETAILS.DISABLED',
                }),
              filterType: 'multiselect',
              auditSignal: true,
            },
            {
              dataField: 'missingActions.AuditAdmin',
              textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.AUDIT_ADMIN_MISSING',
              headerTooltipId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.AUDIT_ADMIN_MISSING_TOOLTIP',
              getValue: (row) => row.missingActions?.AuditAdmin?.join(', '),
              auditSignal: true,
            },
            {
              dataField: 'missingActions.AuditDelegate',
              textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.AUDIT_DELEGATE_MISSING',
              headerTooltipId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.AUDIT_DELEGATE_MISSING_TOOLTIP',
              getValue: (row) => row.missingActions?.AuditDelegate?.join(', '),
              auditSignal: true,
            },
            {
              dataField: 'missingActions.AuditOwner',
              textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.AUDIT_OWNER_MISSING',
              headerTooltipId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.AUDIT_OWNER_MISSING_TOOLTIP',
              getValue: (row) => row.missingActions?.AuditOwner?.join(', '),
              auditSignal: true,
            },
          ],
        },
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'mailboxAuditActions',
        title: 'Mailbox audit actions',
        statusOptions: [
          {
            label: 'Apply CIS mailbox audit actions',
            value: true,
            bestPractice: true,
          },
        ],
        hubTaskType: TASK_TYPES.mailboxAuditActions,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    baseline: {
      mailboxAuditActions: true,
    },
  },
  M365_AUG_CIS_M365_1_3_6: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_AUG_CIS_M365_1_3_6',
    checkName: 'AUG.CIS.M365.1.3.6',
    portal: [PORTALS.M365_ADMIN_CENTER],
    checkModal: { complianceContent: CIS_M365_V6_L2_1_3_6_COMPLIANCE_CONTENT },
    postureTemplate: true,
    properties: [
      {
        name: 'customerLockboxEnabled',
        title: 'Customer Lockbox',
        statusOptions: [
          {
            label: 'Enabled',
            value: true,
            bestPractice: true,
          },
          {
            label: 'Disabled',
            value: false,
          },
        ],
        hubTaskType: TASK_TYPES.customerLockbox,
        hubAction: TASK_ACTIONS.update,
        isDisabled: ({ securityCheck }) => hasCustomerLockboxConfigurationIssues(securityCheck),
        disabledTooltipId: ({ securityCheck }) =>
          hasCustomerLockboxConfigurationIssues(securityCheck)
            ? CUSTOMER_LOCKBOX_MISSING_EXCHANGE_WRITE_PERMISSION_MESSAGE_ID
            : null,
      },
    ],
    baseline: {
      customerLockboxEnabled: true,
    },
  },
  M365_MAILTIPS_ENABLED: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_MAILTIPS_ENABLED',
    checkName: 'mailtipsenabled',
    portal: [PORTALS.EXCHANGE],
    manualSave: true,
    saveButtonId: 'save-mailtips-configuration',
    checkModal: {
      complianceContent: CIS_M365_V6_L1_6_5_2_COMPLIANCE_CONTENT,
      customConfigure: 'mailTips',
      details: {
        markdown: true,
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'mailTipsRecommendedValues',
        title: 'MailTips org-config recommended values',
        displayTitleId: 'SECURE.MAILTIPS.SETTING.RECOMMENDED_VALUES',
        statusOptions: ({ currentSetting }) => [
          ...(currentSetting?.compliant == null
            ? [
                {
                  label: 'Not measured',
                  value: 'Not Measured',
                  isDisabled: true,
                },
              ]
            : []),
          {
            label: 'Applied',
            value: true,
            bestPractice: true,
          },
          {
            label: 'Not applied',
            value: false,
          },
        ],
        hubTaskType: TASK_TYPES.mailTips,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    configureProperties: [
      {
        name: 'mailTipsAllTipsEnabled',
        title: 'MailTipsAllTipsEnabled',
        displayTitleId: 'SECURE.MAILTIPS.SETTING.ALL_TIPS_ENABLED',
        statusOptions: [
          {
            label: 'Enabled',
            value: true,
            bestPractice: true,
          },
          {
            label: 'Disabled',
            value: false,
          },
        ],
        hubTaskType: TASK_TYPES.mailTips,
        hubAction: TASK_ACTIONS.update,
      },
      {
        name: 'mailTipsExternalRecipientsTipsEnabled',
        title: 'MailTipsExternalRecipientsTipsEnabled',
        displayTitleId: 'SECURE.MAILTIPS.SETTING.EXTERNAL_RECIPIENTS_ENABLED',
        statusOptions: [
          {
            label: 'Enabled',
            value: true,
            bestPractice: true,
          },
          {
            label: 'Disabled',
            value: false,
          },
        ],
        hubTaskType: TASK_TYPES.mailTips,
        hubAction: TASK_ACTIONS.update,
      },
      {
        name: 'mailTipsGroupMetricsEnabled',
        title: 'MailTipsGroupMetricsEnabled',
        displayTitleId: 'SECURE.MAILTIPS.SETTING.GROUP_METRICS_ENABLED',
        statusOptions: [
          {
            label: 'Enabled',
            value: true,
            bestPractice: true,
          },
          {
            label: 'Disabled',
            value: false,
          },
        ],
        hubTaskType: TASK_TYPES.mailTips,
        hubAction: TASK_ACTIONS.update,
      },
      {
        name: 'mailTipsLargeAudienceThreshold',
        title: 'MailTipsLargeAudienceThreshold',
        displayTitleId: 'SECURE.MAILTIPS.SETTING.LARGE_AUDIENCE_THRESHOLD',
        controlType: 'number',
        min: 0,
        max: 4294967295,
        step: 1,
        bestPracticeValue: 25,
        bestPracticeMaxValue: 25,
        helpTextId: 'SECURE.MAILTIPS.SETTING.LARGE_AUDIENCE_THRESHOLD_HELP',
        hubTaskType: TASK_TYPES.mailTips,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    baseline: {
      mailTipsRecommendedValues: true,
    },
  },
  M365_USER_OWNED_APPS_AND_SERVICES_RESTRICTED: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_USER_OWNED_APPS_AND_SERVICES_RESTRICTED',
    checkName: 'userownedappsandservicesrestricted',
    portal: [PORTALS.M365_ADMIN_CENTER],
    checkModal: {
      complianceContent: USER_OWNED_APPS_AND_SERVICES_COMPLIANCE_CONTENT,
      customConfigure: 'userOwnedAppsAndServices',
      details: {
        markdown: true,
      },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'appsAndServicesIsOfficeStoreEnabled',
        title: 'Let users access the Office Store',
        statusOptions: [
          {
            label: 'Disabled',
            value: false,
            bestPractice: true,
          },
          {
            label: 'Enabled',
            value: true,
          },
        ],
        hubTaskType: TASK_TYPES.userOwnedAppsAndServices,
        hubAction: TASK_ACTIONS.update,
      },
      {
        name: 'appsAndServicesIsAppAndServicesTrialEnabled',
        title: 'Let users start trials on behalf of your organization',
        statusOptions: [
          {
            label: 'Disabled',
            value: false,
            bestPractice: true,
          },
          {
            label: 'Enabled',
            value: true,
          },
        ],
        hubTaskType: TASK_TYPES.userOwnedAppsAndServices,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    configureProperties: [
      {
        name: 'appsAndServicesIsOfficeStoreEnabled',
        title: 'appsAndServicesIsOfficeStoreEnabled',
        displayTitleId: 'SECURE.USER_OWNED_APPS_AND_SERVICES.SETTING.OFFICE_STORE',
        statusOptions: [
          {
            label: 'Disabled',
            value: false,
            bestPractice: true,
          },
          {
            label: 'Enabled',
            value: true,
          },
        ],
        hubTaskType: TASK_TYPES.userOwnedAppsAndServices,
        hubAction: TASK_ACTIONS.update,
      },
      {
        name: 'appsAndServicesIsAppAndServicesTrialEnabled',
        title: 'appsAndServicesIsAppAndServicesTrialEnabled',
        displayTitleId: 'SECURE.USER_OWNED_APPS_AND_SERVICES.SETTING.APP_TRIALS',
        statusOptions: [
          {
            label: 'Disabled',
            value: false,
            bestPractice: true,
          },
          {
            label: 'Enabled',
            value: true,
          },
        ],
        hubTaskType: TASK_TYPES.userOwnedAppsAndServices,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    baseline: {
      appsAndServicesIsOfficeStoreEnabled: false,
      appsAndServicesIsAppAndServicesTrialEnabled: false,
    },
  },
  M365_FORMS_PHISHING_PROTECTION_ENABLED: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_FORMS_PHISHING_PROTECTION_ENABLED',
    checkName: 'formsphishingprotectionenabled',
    portal: [PORTALS.M365_ADMIN_CENTER],
    checkModal: {
      complianceContent: FORMS_PHISHING_PROTECTION_COMPLIANCE_CONTENT,
      details: { markdown: true },
    },
    postureTemplate: true,
    properties: [
      {
        name: 'formsIsInOrgFormsPhishingScanEnabled',
        title: 'Add internal phishing protection',
        statusOptions: ({ currentSetting }) => [
          ...(currentSetting?.compliant == null
            ? [
                {
                  label: 'Not Measured',
                  value: currentSetting?.status || 'First run pending',
                  isDisabled: true,
                },
              ]
            : []),
          { label: 'Enabled', value: true, bestPractice: true },
          { label: 'Disabled', value: false },
        ],
        hubTaskType: TASK_TYPES.formsSettings,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    baseline: { formsIsInOrgFormsPhishingScanEnabled: true },
  },
  M365_BREAK_GLASS_ACCOUNTS: {
    checkName: 'breakglassaccounts',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BREAKGLASSACCOUNTS.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BREAKGLASSACCOUNTS.NIST_CSF_2_0.DESCRIPTION',
        },
      },
      customConfigure: 'breakGlassAccounts',
      details: {
        markdown: true,
      },
    },
  },
  M365_MS_EXO_12_1: {
    checkName: 'CISA.MS.EXO.12.1',
    portal: [PORTALS.DEFENDER],
    checkModal: { complianceContent: CIS_M365_V6_L1_6_2_2_COMPLIANCE_CONTENT },
  },
  M365_MS_EXO_12_2: {
    checkName: 'CISA.MS.EXO.12.2',
    portal: [PORTALS.DEFENDER],
    checkModal: { complianceContent: CIS_M365_V6_L1_6_2_2_COMPLIANCE_CONTENT },
  },
  M365_MS_AAD_7_4: {
    checkName: 'CISA.MS.AAD.7.4',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L2_5_3_1_COMPLIANCE_CONTENT,
        ...CISA_CISAMSAAD74_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD74.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD74.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_MT_1031: {
    checkName: 'MT.1031',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L2_5_3_1_COMPLIANCE_CONTENT,
        ...essential8Content(
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MT1031.ESSENTIAL_8.TITLE',
          'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MT1031.ESSENTIAL_8.DESCRIPTION',
        ),
      },
    },
  },
  M365_MDO_ALLOWEDSENDERSCOMBINED: {
    checkName: 'mdo_allowedsenderscombined',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: {
        ...CIS_M365_V6_L1_2_1_14_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOALLOWEDSENDERSCOMBINED.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOALLOWEDSENDERSCOMBINED.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  // AP-12415: route the remaining anti-spam inbound checks to the Defender Configure panel.
  // These checks already resolve settings via secure-score-profile.js; they were simply
  // missing this policyType wiring. No complianceContent yet — pre-existing gap, out of scope here.
  M365_MDO_SPAMACTION: {
    checkName: 'mdo_spamaction',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      complianceContent: {
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSPAMACTION.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOSPAMACTION.NIST_CSF_2_0.DESCRIPTION',
        },
      },
      policyType: 'defender',
    },
  },
  M365_MDO_HIGHCONFIDENCESPAMACTION: {
    checkName: 'mdo_highconfidencespamaction',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      complianceContent: {
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOHIGHCONFIDENCESPAMACTION.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOHIGHCONFIDENCESPAMACTION.NIST_CSF_2_0.DESCRIPTION',
        },
      },
      policyType: 'defender',
    },
  },
  M365_MDO_PHISSPAMACATION: {
    checkName: 'mdo_phisspamacation',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      complianceContent: {
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOPHISSPAMACATION.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOPHISSPAMACATION.NIST_CSF_2_0.DESCRIPTION',
        },
      },
      policyType: 'defender',
    },
  },
  M365_MDO_HIGHCONFIDENCEPHISHACTION: {
    checkName: 'mdo_highconfidencephishaction',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      complianceContent: {
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOHIGHCONFIDENCEPHISHACTION.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOHIGHCONFIDENCEPHISHACTION.NIST_CSF_2_0.DESCRIPTION',
        },
      },
      policyType: 'defender',
    },
  },
  M365_MDO_BULKSPAMACTION: {
    checkName: 'mdo_bulkspamaction',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      complianceContent: {
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOBULKSPAMACTION.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOBULKSPAMACTION.NIST_CSF_2_0.DESCRIPTION',
        },
      },
      policyType: 'defender',
    },
  },
  M365_MDO_BULKTHRESHOLD: {
    checkName: 'mdo_bulkthreshold',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      complianceContent: {
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOBULKTHRESHOLD.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOBULKTHRESHOLD.NIST_CSF_2_0.DESCRIPTION',
        },
      },
      policyType: 'defender',
    },
  },
  M365_MDO_QUARANTINERETENTIONPERIOD: {
    checkName: 'mdo_quarantineretentionperiod',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      complianceContent: {
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOQUARANTINERETENTIONPERIOD.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOQUARANTINERETENTIONPERIOD.NIST_CSF_2_0.DESCRIPTION',
        },
      },
      policyType: 'defender',
    },
  },
  M365_MDO_ZAPPHISH: {
    checkName: 'mdo_zapphish',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
    },
  },
  M365_MDO_ZAPSPAM: {
    checkName: 'mdo_zapspam',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      complianceContent: {
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOZAPSPAM.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOZAPSPAM.NIST_CSF_2_0.DESCRIPTION',
        },
      },
      policyType: 'defender',
    },
  },
  M365_BLOCK_SIGN_IN_SHARED_MAILBOXES: {
    checkName: 'blocksigninsharedmailboxes',
    portal: [PORTALS.M365_ADMIN_CENTER, PORTALS.ENTRA],
    checkModal: {
      customConfigure: 'blockSignInSharedMailboxes',
      complianceContent: {
        ...CIS_M365_V6_L1_1_2_2_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKSIGNINSHAREDMAILBOXES.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKSIGNINSHAREDMAILBOXES.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKSIGNINSHAREDMAILBOXES.NIST_CSF_2_0.TITLE',
          descriptionId:
            'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKSIGNINSHAREDMAILBOXES.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKSIGNINSHAREDMAILBOXES.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKSIGNINSHAREDMAILBOXES.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKSIGNINSHAREDMAILBOXES.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.BLOCKSIGNINSHAREDMAILBOXES.CMMC_L2.DESCRIPTION',
        },
      },
      configure: {
        entities: {
          keyField: 'userId',
          labelId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.SHARED_MAILBOXES',
          columns: [
            {
              dataField: 'displayName',
              textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.MAILBOX',
              cellType: 'employeeName',
              getValue: (row) => row.displayName?.replace(SHARED_MAILBOX_NAME_SUFFIX, ''),
              sortable: true,
              searchable: true,
            },
            {
              dataField: 'isUserSigninEnabled',
              textId: 'COMPLIANCE_AUDIT.CHECK.DETAILS.SIGN_IN_STATUS',
              getValue: allowBlockStatus('isUserSigninEnabled', true),
              auditSignal: true,
            },
          ],
        },
      },
    },
  },
  M365_EXTERNAL_MAIL_FORWARDING: {
    checkName: 'externalmailforwarding',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_6_2_1_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EXTERNALMAILFORWARDING.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.EXTERNALMAILFORWARDING.NIST_CSF_2_0.DESCRIPTION',
        },
        ...CISA_EXTERNALMAILFORWARDING_COMPLIANCE_CONTENT,
      },
    },
  },
  M365_SAFE_ATTACHMENT: {
    checkName: 'safeattachment',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L2_2_1_5_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SAFEATTACHMENT.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SAFEATTACHMENT.HIPAA.DESCRIPTION',
        },
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SAFEATTACHMENT.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SAFEATTACHMENT.NIST_CSF_2_0.DESCRIPTION',
        },
        ...CISA_SAFEATTACHMENT_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L1]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SAFEATTACHMENT.CMMC_L1.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SAFEATTACHMENT.CMMC_L1.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SAFEATTACHMENT.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.SAFEATTACHMENT.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_MDO_CONNECTIONFILTER: {
    checkName: 'mdo_connectionfilter',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L1_2_1_12_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOCONNECTIONFILTER.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MDOCONNECTIONFILTER.NIST_CSF_2_0.DESCRIPTION',
        },
      },
    },
  },
  M365_AUG_CIS_M365_2_1_13: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_AUG_CIS_M365_2_1_13',
    checkName: 'AUG.CIS.M365.2.1.13',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      complianceContent: CIS_M365_V6_L1_2_1_13_COMPLIANCE_CONTENT,
    },
    postureTemplate: true,
    properties: [
      {
        name: 'enableSafeList',
        title: 'Connection filter safe list',
        statusOptions: [
          {
            label: 'Off',
            value: false,
            bestPractice: true,
          },
          {
            label: 'On',
            value: true,
          },
        ],
        hubTaskType: TASK_TYPES.connectionFilterSafeList,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    baseline: {
      enableSafeList: false,
    },
  },
  M365_CONNECTION_FILTER_IP_ALLOW_LIST: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_CONNECTION_FILTER_IP_ALLOW_LIST',
    checkName: 'connectionfilteripallowlist',
    portal: [PORTALS.DEFENDER],
    postureTemplate: true,
    hubTaskType: TASK_TYPES.connectionFilterIpAllowList,
    hubAction: TASK_ACTIONS.update,
    properties: [
      {
        name: 'ipAllowList',
        title: 'Connection Filter IP Allow List',
        statusOptions: [{ label: 'Cleared', value: [], bestPractice: true }],
        hubTaskType: TASK_TYPES.connectionFilterIpAllowList,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    checkModal: {
      complianceContent: CONNECTION_FILTER_IP_ALLOW_LIST_COMPLIANCE_CONTENT,
      customConfigure: 'connectionFilterIpAllowList',
      details: { markdown: true },
    },
  },
  M365_COMPREHENSIVE_ATTACHMENT_FILTERING: {
    checkName: 'comprehensiveattachmentfiltering',
    portal: [PORTALS.DEFENDER],
    checkModal: {
      policyType: 'defender',
      complianceContent: COMPREHENSIVE_ATTACHMENT_FILTERING_COMPLIANCE_CONTENT,
      details: { markdown: true },
    },
  },
  M365_INBOUND_ANTI_SPAM_ALLOWED_DOMAINS: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_INBOUND_ANTI_SPAM_ALLOWED_DOMAINS',
    checkName: 'inboundantispamalloweddomains',
    portal: [PORTALS.DEFENDER],
    postureTemplate: true,
    hubTaskType: TASK_TYPES.inboundAntiSpamAllowedDomains,
    hubAction: TASK_ACTIONS.update,
    properties: [
      {
        name: 'actionName',
        title: 'Inbound Anti-Spam Allowed Domains',
        statusOptions: [{ label: 'Cleared', value: 'Apply recommended fix', bestPractice: true }],
        hubTaskType: TASK_TYPES.inboundAntiSpamAllowedDomains,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    checkModal: {
      complianceContent: INBOUND_ANTI_SPAM_ALLOWED_DOMAINS_COMPLIANCE_CONTENT,
      customConfigure: 'inboundAntiSpamAllowedDomains',
      details: { markdown: true },
    },
  },
  M365_OUTBOUND_ANTI_SPAM_MESSAGE_LIMITS: {
    integrationType: 'Office365',
    mgmTaskId: 'M365_SECURE_PARTIAL',
    taskName: 'M365_OUTBOUND_ANTI_SPAM_MESSAGE_LIMITS',
    checkName: 'outboundantispammessagelimits',
    portal: [PORTALS.DEFENDER],
    postureTemplate: true,
    hubTaskType: TASK_TYPES.outboundAntiSpamMessageLimits,
    hubAction: TASK_ACTIONS.update,
    properties: [
      {
        name: 'recipientLimitExternalPerHour',
        title: 'Outbound Anti-Spam External Recipient Limit',
        statusOptions: [{ label: '500', value: 500, bestPractice: true }],
        hubTaskType: TASK_TYPES.outboundAntiSpamMessageLimits,
        hubAction: TASK_ACTIONS.update,
      },
      {
        name: 'recipientLimitInternalPerHour',
        title: 'Outbound Anti-Spam Internal Recipient Limit',
        statusOptions: [{ label: '1000', value: 1000, bestPractice: true }],
        hubTaskType: TASK_TYPES.outboundAntiSpamMessageLimits,
        hubAction: TASK_ACTIONS.update,
      },
      {
        name: 'recipientLimitPerDay',
        title: 'Outbound Anti-Spam Daily Recipient Limit',
        statusOptions: [{ label: '1000', value: 1000, bestPractice: true }],
        hubTaskType: TASK_TYPES.outboundAntiSpamMessageLimits,
        hubAction: TASK_ACTIONS.update,
      },
      {
        name: 'actionWhenThresholdReached',
        title: 'Outbound Anti-Spam Threshold Action',
        statusOptions: [{ label: 'BlockUser', value: 'BlockUser', bestPractice: true }],
        hubTaskType: TASK_TYPES.outboundAntiSpamMessageLimits,
        hubAction: TASK_ACTIONS.update,
      },
    ],
    checkModal: {
      complianceContent: OUTBOUND_ANTI_SPAM_MESSAGE_LIMITS_COMPLIANCE_CONTENT,
      customConfigure: 'outboundAntiSpamMessageLimits',
    },
  },
  M365_MS_EXO_2_1: {
    checkName: 'CISA.MS.EXO.2.1',
    portal: [PORTALS.DNS_EXTERNAL, PORTALS.EXCHANGE],
    checkModal: {
      complianceContent: CIS_M365_V6_L1_2_1_12_COMPLIANCE_CONTENT,
    },
  },
  M365_MS_AAD_7_5: {
    checkName: 'CISA.MS.AAD.7.5',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L2_5_3_1_COMPLIANCE_CONTENT,
        ...CISA_CISAMSAAD75_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD75.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD75.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_MS_AAD_7_6: {
    checkName: 'CISA.MS.AAD.7.6',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        ...CIS_M365_V6_L2_5_3_1_COMPLIANCE_CONTENT,
        ...CISA_CISAMSAAD76_COMPLIANCE_CONTENT,
        [COMPLIANCE_TYPES.CMMC_L2]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD76.CMMC_L2.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.CISAMSAAD76.CMMC_L2.DESCRIPTION',
        },
      },
    },
  },
  M365_EIDSCA_AF01: {
    checkName: 'EIDSCA.AF01',
    portal: [PORTALS.ENTRA],
  },
  M365_EIDSCA_AF02: {
    checkName: 'EIDSCA.AF02',
    portal: [PORTALS.ENTRA],
  },
  M365_EIDSCA_AG02: {
    checkName: 'EIDSCA.AG02',
    portal: [PORTALS.ENTRA],
  },
  M365_EIDSCA_AP09: {
    checkName: 'EIDSCA.AP09',
    portal: [PORTALS.ENTRA],
  },
  M365_EIDSCA_AT02: {
    checkName: 'EIDSCA.AT02',
    portal: [PORTALS.ENTRA],
  },
  M365_EIDSCA_AV01: {
    checkName: 'EIDSCA.AV01',
    portal: [PORTALS.ENTRA],
  },
  M365_EIDSCA_CP03: {
    checkName: 'EIDSCA.CP03',
    portal: [PORTALS.ENTRA],
  },
  M365_EIDSCA_CR02: {
    checkName: 'EIDSCA.CR02',
    portal: [PORTALS.ENTRA],
  },
  M365_EIDSCA_CR03: {
    checkName: 'EIDSCA.CR03',
    portal: [PORTALS.ENTRA],
  },
  M365_EIDSCA_CR04: {
    checkName: 'EIDSCA.CR04',
    portal: [PORTALS.ENTRA],
  },
  M365_MT_1002: {
    checkName: 'MT.1002',
    portal: [PORTALS.ENTRA],
  },
  M365_MT_1003: {
    checkName: 'MT.1003',
    portal: [PORTALS.ENTRA],
  },
  M365_MT_1004: {
    checkName: 'MT.1004',
    portal: [PORTALS.ENTRA],
  },
  M365_MT_1005: {
    checkName: 'MT.1005',
    portal: [PORTALS.ENTRA],
  },
  M365_MT_1018: {
    checkName: 'MT.1018',
    portal: [PORTALS.ENTRA],
  },
  M365_MT_1019: {
    checkName: 'MT.1019',
    portal: [PORTALS.ENTRA, PORTALS.SHAREPOINT],
  },
  M365_MT_1020: {
    checkName: 'MT.1020',
    portal: [PORTALS.ENTRA],
  },
  M365_MT_1022: {
    checkName: 'MT.1022',
    portal: [PORTALS.M365_ADMIN_CENTER, PORTALS.ENTRA],
  },
  M365_MT_1023: {
    checkName: 'MT.1023',
    portal: [PORTALS.M365_ADMIN_CENTER, PORTALS.ENTRA],
  },
  M365_CONTACT_SETTINGS: {
    checkName: 'contactsettings',
    portal: [PORTALS.M365_ADMIN_CENTER],
  },
  M365_LOGIN_PORTAL_BRANDING: {
    checkName: 'loginportalbranding',
    portal: [PORTALS.ENTRA],
  },
  M365_MFA_CAMPAIGN: {
    checkName: 'mfacampaign',
    portal: [PORTALS.ENTRA],
    checkModal: {
      complianceContent: {
        [COMPLIANCE_TYPES['NIST_CSF_2.0']]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MFACAMPAIGN.NIST_CSF_2_0.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MFACAMPAIGN.NIST_CSF_2_0.DESCRIPTION',
        },
        [COMPLIANCE_TYPES.HIPAA]: {
          titleId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MFACAMPAIGN.HIPAA.TITLE',
          descriptionId: 'COMPLIANCE_AUDIT.CHECK.OVERVIEW.COMPLIANCE.MFACAMPAIGN.HIPAA.DESCRIPTION',
        },
      },
    },
  },
}

export { preTaskDefinitions }
